import * as THREE from "three";
import GUI from "lil-gui";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { Sky } from "three/examples/jsm/Addons.js";
import { createGroundToonMaterial, createGateToonMaterial } from "../materials.js";
import { createPathTrail } from "../pathTrail.js";
import { createClouds } from "../clouds.js";
import { loadingOverlay } from "../loadingOverlay.js";
import { isTouchDevice } from "../deviceUtils.js";
import { createMusicController } from "../audio.js";
import { createSfxController } from "../sfx.js";

// Append ?debug=1 to the URL to get the old lil-gui tuning panel back.
const DEBUG_GUI = new URLSearchParams(location.search).has("debug");
const IS_TOUCH_DEVICE = isTouchDevice();

const ROUND_END_DELAY_MS = 1400;

// The menu is just another mode of this same screen now, so the earth the
// player sees behind the PLAY button is the literal gameplay earth/scene -
// not a second copy loaded separately - and pressing PLAY simply swings the
// camera from its orbiting showcase pose into the gameplay chase pose.
const menuEl = document.getElementById("screen-menu");
const playBtn = document.getElementById("btn-play");

// Rules modal is static menu chrome (not tied to a screen's enter/exit
// lifecycle), so it's wired up once here at module load rather than inside
// createGameScreen().
const rulesBtn = document.getElementById("btn-rules");
const rulesModal = document.getElementById("rules-modal");
const rulesCloseBtn = document.getElementById("btn-rules-close");
rulesBtn?.addEventListener("click", () => rulesModal.classList.add("visible"));
rulesCloseBtn?.addEventListener("click", () => rulesModal.classList.remove("visible"));
rulesModal?.addEventListener("click", (e) => {
  if (e.target === rulesModal) rulesModal.classList.remove("visible");
});
// Only one control scheme applies on any given device - drop the other
// list rather than showing both and making the player figure out which is theirs.
rulesModal?.querySelector(`[data-controls="${IS_TOUCH_DEVICE ? "keyboard" : "mobile"}"]`)?.remove();

// Music is likewise page-level chrome rather than per-screen state - it
// starts trying to play the moment the page loads and keeps going (or
// stays silently blocked, per the browser's autoplay policy) across every
// screen transition. PLAY counts as a user gesture too, so it gets a
// second chance to unblock autoplay if the initial attempt was rejected.
const muteBtn = document.getElementById("btn-mute");
const musicController = createMusicController(muteBtn);
const sfx = createSfxController();

// PLAY's click handler lives inside createGameScreen(), alongside
// requestStartGame() (which it's merged with, to sequence the pre-round
// countdown around this) - see there.

/**
 * Factory instead of a class: almost everything below is a straight port
 * of the original script.js, just moved from module-level `let`s into
 * variables closed over by this function so the whole game can be
 * created once and safely re-entered every time the player hits PLAY.
 *
 * onRoundStart(): called once per round, the moment gameplay actually
 *   begins (right as resetRound() runs) - before the countdown/timer starts
 *   ticking client-side.
 * onRoundFinish(): called the instant the round is decided - the hider is
 *   caught or the timer runs out - synchronously, before the celebratory
 *   pause. This is deliberately earlier than onRoundEnd: it's the hook for
 *   stamping a server-side end time (see endRound() in supabaseClient.js),
 *   which needs to land as close as possible to this exact moment rather
 *   than after ROUND_END_DELAY_MS has also elapsed.
 * onRoundEnd({ elapsedSeconds, found }): called once when the hider is
 *   caught or the timer runs out, after a short celebratory pause.
 */
export function createGameScreen({ onRoundStart, onRoundFinish, onRoundEnd }) {
  let assetsLoaded = false; // true once EVERYTHING (incl. player/hider/pickup/trivia) is ready to actually play
  let assetsLoadingPromise = null;
  let criticalAssetsLoaded = false; // true once just the menu's visible backdrop (sky + maze) is ready
  let criticalAssetsPromise = null;
  let isStartingRound = false; // guards beginRoundStart() against a repeat PLAY press while assets finish loading
  let gui = null;

  let scene = null;
  let camera = null;
  let earthMesh = null;
  let renderer = null;

  /* ---------------- player / world constants ---------------- */
  const sphereRadius = 5;
  const playerCollisionRadius = 0.15;

  const params = {
    playerModelScale: 0.25,
    playerMoveSpeed: 1.8,
    hiderModelScale: 0.25,
    hiderMoveSpeed: 0.85,
  };

  const playerState = {
    position: new THREE.Vector3(0, sphereRadius, 0),
    forward: new THREE.Vector3(-1, 0, 0),
    height: 0,
    verticalVelocity: 0,
    isGrounded: true,
    jumpsUsed: 0, // 0 = grounded, 1 = single jump used, 2 = double jump used (reset on landing)
  };

  function computeInitialForward(position) {
    const up = getUp(position);
    const forwardDir = new THREE.Vector3(1, 0, 0).cross(up).normalize();
    return forwardDir.length() > 0.1 ? forwardDir : new THREE.Vector3(0, 0, -1);
  }

  // "menu": orbiting showcase camera, PLAY button visible, gameplay input off.
  // "transition": PLAY was pressed - camera is flying from the showcase pose
  //   into the gameplay chase pose, over TRANSITION_DURATION seconds.
  // "play": normal gameplay.
  // "toEnd": the round just ended - camera is flying from wherever the
  //   chase cam was back to the showcase pose, over TRANSITION_DURATION
  //   seconds, while the leaderboard overlay is already showing.
  // "end": round-end showcase, same orbiting camera as "menu" but with the
  //   leaderboard overlay showing instead of the PLAY button.
  let mode = "menu";
  let transitionElapsed = 0;
  const TRANSITION_DURATION = 1.3;

  let menuOrbitAngle = 0;
  const menuOrbitRadius = 9.5;
  const menuOrbitHeight = 5.5;
  const menuLookAtHeight = 0; // aim at the globe's true centre so it sits centred in frame, with the UI floating over it
  const menuOrbitSpeed = 0.1;

  let birdseyeMode = false;
  // Set while the player has pressed E and the Throw wind-up is playing but
  // birdseye hasn't actually kicked in yet - see toggleBirdseye() and the
  // mixer "finished" listener that flips this into the real birdseyeMode
  // once the clip completes.
  let birdseyeEntryPending = false;
  const birdseyeState = { position: new THREE.Vector3(), forward: new THREE.Vector3() };
  const birdseyeHeight = 9;
  const birdseyeMoveSpeed = 5;
  const birdseyeTurnSpeed = 2.2;

  const turnSpeed = 3.5;
  const gravity = 9;
  const jumpStrength = 3;
  const doubleJumpStrafeDistance = 0.22; // small forward hop, not a dash - see tryJump()
  // Swept in small increments (rather than one teleport) and resolved against
  // walls after each one, same as continuous per-frame movement - a single
  // large step could land clean on the far side of a thin wall without ever
  // registering as "close" to it, tunnelling straight through.
  const doubleJumpStrafeSteps = 6;

  function getUp(position) { return position.clone().normalize(); }

  /* ---------------- terrain-aware surface radius ---------------- */
  // NOTE: this is what makes the earth's flat top (or any other
  // non-spherical bump/dent) actually get walked on and collided with -
  // getSurfaceRadius()/projectToSurface() interpolate the *real* mesh
  // radius from terrainHeightMap rather than assuming a perfect sphere of
  // radius `sphereRadius`. sphereRadius is only ever used as a fallback
  // and as a ray-cast starting distance, never as the "ground truth"
  // shape once the height map is built.
  const terrainGridWidth = 220;
  const terrainGridHeight = 110;
  let terrainHeightMap = null;

  function getSurfaceRadius(direction) {
    if (!terrainHeightMap) return sphereRadius;
    const theta = (Math.atan2(direction.z, direction.x) + Math.PI * 2) % (Math.PI * 2);
    const phi = Math.acos(THREE.MathUtils.clamp(direction.y, -1, 1));
    const fx = (theta / (Math.PI * 2)) * terrainGridWidth;
    const fy = (phi / Math.PI) * terrainGridHeight;
    const x0 = Math.floor(fx) % terrainGridWidth;
    const x1 = (x0 + 1) % terrainGridWidth;
    const y0 = THREE.MathUtils.clamp(Math.floor(fy), 0, terrainGridHeight - 1);
    const y1 = THREE.MathUtils.clamp(y0 + 1, 0, terrainGridHeight - 1);
    const tx = fx - Math.floor(fx);
    const ty = fy - Math.floor(fy);
    const r00 = terrainHeightMap[y0 * terrainGridWidth + x0];
    const r10 = terrainHeightMap[y0 * terrainGridWidth + x1];
    const r01 = terrainHeightMap[y1 * terrainGridWidth + x0];
    const r11 = terrainHeightMap[y1 * terrainGridWidth + x1];
    const rTop = THREE.MathUtils.lerp(r00, r10, tx);
    const rBottom = THREE.MathUtils.lerp(r01, r11, tx);
    return THREE.MathUtils.lerp(rTop, rBottom, ty);
  }

  function projectToSurface(position) {
    const direction = position.clone().normalize();
    return direction.multiplyScalar(getSurfaceRadius(direction));
  }

  function getRenderPosition() {
    const surfacePoint = projectToSurface(playerState.position);
    const up = getUp(playerState.position);
    return surfacePoint.addScaledVector(up, playerState.height);
  }

  function syncPlayerModelTransform() {
    if (!model) return;
    const up = getUp(playerState.position);
    const renderPosition = getRenderPosition();
    model.position.copy(renderPosition);
    model.up.copy(up);
    model.lookAt(renderPosition.clone().add(playerState.forward));
  }

  /** Second press while airborne (one shot per landing) doesn't jump again -
   * it just strafes the player forward, a small burst along their current
   * facing, resolved through the same wall-collision/surface-projection
   * path normal movement uses so it can't punch through a gate or wall. */
  function tryJump() {
    if (playerState.isGrounded) {
      playerState.verticalVelocity = jumpStrength;
      playerState.isGrounded = false;
      playerState.jumpsUsed = 1;
      playJumpAnimation();
      sfx.playJump();
    } else if (playerState.jumpsUsed === 1) {
      playerState.jumpsUsed = 2;
      sfx.playStrafe();
      const stepDistance = doubleJumpStrafeDistance / doubleJumpStrafeSteps;
      for (let i = 0; i < doubleJumpStrafeSteps; i++) {
        const proposedSurface = projectToSurface(playerState.position.clone().addScaledVector(playerState.forward, stepDistance));
        playerState.position.copy(resolveWallCollisions(proposedSurface));
      }
    }
  }

  /* ---------------- input ---------------- */
  const keysPressed = {};
  function onKeyDown(e) {
    if (mode !== "play") return;
    const key = e.key.toLowerCase();
    if (key.startsWith("arrow")) e.preventDefault(); // don't let the page scroll
    keysPressed[key] = true;

    if (isTriviaModalOpen) {
      const optionIndex = Number(key) - 1;
      if (currentGate && optionIndex >= 0 && optionIndex < currentGate.question.options.length) answerTriviaOption(optionIndex);
      return;
    }

    // Space and E are the same two multipurpose buttons as mobile's A/B (see
    // handleActionButton/handleBirdseyeButton below) - Space jumps and does
    // whatever contextual action applies (answer a gate, mark a path in
    // birdseye, or catch Bliss), E toggles birdseye. Kept as separate keys
    // here only so held-key repeat doesn't spam actions; the actual logic is
    // shared with mobile so both inputs stay in sync.
    if (key === " " && !e.repeat) { e.preventDefault(); handleActionButton(); }
    if (key === "e" && !e.repeat) handleBirdseyeButton();
  }
  function onKeyUp(e) { keysPressed[e.key.toLowerCase()] = false; }

  function attachInput() {
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
  }
  function detachInput() {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    for (const key of Object.keys(keysPressed)) keysPressed[key] = false;
  }

  /* ---------------- mobile touch controls ---------------- */
  // Analog joystick deflection, -1..1 per axis (x: right+, y: forward+) -
  // blended with keysPressed below so keyboard and touch just add together
  // and desktop behaviour (turn/move always exactly -1/0/1) is unchanged.
  const joystickVector = { x: 0, y: 0 };
  let joystickPointerId = null;
  const JOYSTICK_RADIUS = 84; // px - keep in sync with .joystick-base size in style.css
  // Reaching true full deflection means dragging all the way to the visual
  // edge, which most thumb drags never do - raw linear input then feels
  // sluggish since it's rarely above ~50% speed. A deadzone plus an
  // exponent <1 on the magnitude ramps partial drags up to full speed much
  // sooner (the knob itself still tracks the finger 1:1, only the derived
  // move/turn strength is curved), while keyboard input is untouched.
  const JOYSTICK_DEADZONE = 0.05;
  const JOYSTICK_RESPONSE_CURVE = 0.45;

  function getMoveInput() {
    let turn = 0, move = 0;
    if (keysPressed["a"] || keysPressed["arrowleft"]) turn += 1;
    if (keysPressed["d"] || keysPressed["arrowright"]) turn -= 1;
    if (keysPressed["w"] || keysPressed["arrowup"]) move += 1;
    if (keysPressed["s"] || keysPressed["arrowdown"]) move -= 1;
    turn = THREE.MathUtils.clamp(turn - joystickVector.x, -1, 1);
    move = THREE.MathUtils.clamp(move + joystickVector.y, -1, 1);
    return { turn, move };
  }

  /** Shared by both keyboard (Space) and mobile (button A) - doubles up as
   * jump and a single contextual action: answer a nearby gate, else mark a
   * path while in birdseye, else try to catch Bliss. Jump and the
   * contextual action never conflict (tryJump() is a no-op unless
   * grounded), so both fire from the same press instead of needing a
   * second control - except a gate or a catchable Bliss: those are checked
   * first and return early, so neither also fires a jump on the same press
   * (for the gate, jump physics keep running while the modal freezes player
   * movement, so a jump triggered on the same press would visibly finish
   * playing out only after the modal closes; for Bliss it's just cleaner
   * for the catch to read as a clean catch, not a catch-plus-jump). */
  function handleActionButton() {
    if (mode !== "play" || isTriviaModalOpen || birdseyeEntryPending || isCatching) return;
    // Catching Bliss takes priority over a gate when both are in range at
    // once (updateGateProximity() already keeps activeGate clear in that
    // case - this ordering is belt-and-suspenders for the same intent).
    if (isNearHider) { tryCatchHider(); return; }
    if (activeGate) { tryOpenNearbyGate(); return; }
    // Checked before tryJump() (not after, like the gate/hider cases above)
    // because movement/gravity stay frozen the whole time birdseyeMode is
    // on - queuing a jump here would just sit invisibly on isGrounded/
    // verticalVelocity until birdseye exits, then suddenly play out the
    // instant the freeze lifts, as an unexplained jump right after Catch.
    if (birdseyeMode) { markPathToAimPoint(); return; }
    tryJump();
    tryCatchHider();
  }
  /** Shared by both keyboard (E) and mobile (button B). */
  function handleBirdseyeButton() {
    if (mode !== "play" || isTriviaModalOpen) return;
    toggleBirdseye();
  }

  function setJoystickKnobOffset(dx, dy) {
    joystickKnob.style.transform = `translate(${dx}px, ${dy}px)`;
  }
  function resetJoystick() {
    joystickPointerId = null;
    joystickVector.x = 0;
    joystickVector.y = 0;
    setJoystickKnobOffset(0, 0);
    joystickBase.classList.remove("active");
  }
  function updateJoystickFromPointer(e) {
    const rect = joystickBase.getBoundingClientRect();
    let dx = e.clientX - (rect.left + rect.width / 2);
    let dy = e.clientY - (rect.top + rect.height / 2);
    const dist = Math.hypot(dx, dy);
    const clampedDist = Math.min(dist, JOYSTICK_RADIUS);
    if (dist > JOYSTICK_RADIUS) {
      dx = (dx / dist) * JOYSTICK_RADIUS;
      dy = (dy / dist) * JOYSTICK_RADIUS;
    }
    setJoystickKnobOffset(dx, dy);

    const rawMagnitude = clampedDist / JOYSTICK_RADIUS;
    const magnitude = rawMagnitude < JOYSTICK_DEADZONE
      ? 0
      : Math.pow((rawMagnitude - JOYSTICK_DEADZONE) / (1 - JOYSTICK_DEADZONE), JOYSTICK_RESPONSE_CURVE);
    const angle = Math.atan2(dy, dx);
    joystickVector.x = Math.cos(angle) * magnitude;
    joystickVector.y = -Math.sin(angle) * magnitude;
  }
  function onJoystickPointerDown(e) {
    if (joystickPointerId !== null) return;
    joystickPointerId = e.pointerId;
    joystickBase.setPointerCapture(e.pointerId);
    joystickBase.classList.add("active");
    updateJoystickFromPointer(e);
  }
  function onJoystickPointerMove(e) {
    if (e.pointerId !== joystickPointerId) return;
    updateJoystickFromPointer(e);
  }
  function onJoystickPointerEnd(e) {
    if (e.pointerId !== joystickPointerId) return;
    resetJoystick();
  }

  function attachMobileControlEvents() {
    joystickBase.addEventListener("pointerdown", onJoystickPointerDown);
    joystickBase.addEventListener("pointermove", onJoystickPointerMove);
    joystickBase.addEventListener("pointerup", onJoystickPointerEnd);
    joystickBase.addEventListener("pointercancel", onJoystickPointerEnd);
    mobileBtnA.addEventListener("pointerdown", (e) => { e.preventDefault(); handleActionButton(); });
    mobileBtnB.addEventListener("pointerdown", (e) => { e.preventDefault(); handleBirdseyeButton(); });
  }

  function showMobileControls() {
    if (!joystickBase) return;
    joystickBase.style.display = "block";
    mobileActions.style.display = "block";
  }
  function hideMobileControls() {
    if (!joystickBase) return;
    resetJoystick();
    joystickBase.style.display = "none";
    mobileActions.style.display = "none";
  }

  /* ---------------- DOM overlays (created once, attached/detached per round) ---------------- */
  let crosshairElement, birdseyeHudElement, gatePromptElement, catchPromptElement, countdownWrapElement, countdownElement, countdownBonusElement;
  let triviaOverlay, triviaBox, triviaPrompt, triviaOptionsContainer;
  let joystickBase, joystickKnob, mobileActions, mobileBtnB, mobileBtnA;
  const overlayEls = [];

  function buildOverlays() {
    crosshairElement = document.createElement("div");
    crosshairElement.className = "crosshair";
    crosshairElement.innerHTML = `
      <svg viewBox="0 0 22 22" width="22" height="22">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.5" />
        <line x1="11" y1="0" x2="11" y2="6" stroke="currentColor" stroke-width="1.5" />
        <line x1="11" y1="16" x2="11" y2="22" stroke="currentColor" stroke-width="1.5" />
        <line x1="0" y1="11" x2="6" y2="11" stroke="currentColor" stroke-width="1.5" />
        <line x1="16" y1="11" x2="22" y2="11" stroke="currentColor" stroke-width="1.5" />
      </svg>`;

    // Birdseye reads as looking through a hat-cam: a green tint over the
    // whole view, a "CAP CAM" watermark, and four corner brackets - see
    // showBirdseyeHud()/hideBirdseyeHud().
    birdseyeHudElement = document.createElement("div");
    birdseyeHudElement.className = "birdseye-hud";
    birdseyeHudElement.innerHTML = `
      <div class="birdseye-hud-corner corner-tl"></div>
      <div class="birdseye-hud-corner corner-tr"></div>
      <div class="birdseye-hud-corner corner-bl"></div>
      <div class="birdseye-hud-corner corner-br"></div>
      <div class="birdseye-hud-label">CAP CAM</div>`;

    triviaOverlay = document.createElement("div");
    triviaOverlay.className = "trivia-overlay";
    triviaBox = document.createElement("div");
    triviaBox.className = "trivia-box";
    triviaPrompt = document.createElement("div");
    triviaPrompt.className = "trivia-prompt";
    triviaOptionsContainer = document.createElement("div");
    triviaOptionsContainer.className = "trivia-options";
    triviaBox.appendChild(triviaPrompt);
    triviaBox.appendChild(triviaOptionsContainer);
    triviaOverlay.appendChild(triviaBox);

    gatePromptElement = document.createElement("div");
    gatePromptElement.className = "gate-prompt";
    gatePromptElement.textContent = IS_TOUCH_DEVICE ? "Press A to answer" : "Press SPACE to answer";

    catchPromptElement = document.createElement("div");
    catchPromptElement.className = "gate-prompt";
    catchPromptElement.textContent = IS_TOUCH_DEVICE ? "Press A to find Bliss!" : "Press SPACE to find Bliss!";

    // Countdown number + its "+10" bonus popup live in one fixed-position
    // wrapper so the popup can sit right next to the number without each
    // needing to track the other's layout - see flashCountdownBonus().
    countdownWrapElement = document.createElement("div");
    countdownWrapElement.className = "countdown-wrap";
    countdownElement = document.createElement("div");
    countdownElement.className = "countdown-badge";
    countdownBonusElement = document.createElement("div");
    countdownBonusElement.className = "countdown-bonus";
    countdownWrapElement.appendChild(countdownElement);
    countdownWrapElement.appendChild(countdownBonusElement);

    overlayEls.push(crosshairElement, birdseyeHudElement, triviaOverlay, gatePromptElement, catchPromptElement, countdownWrapElement);

    if (IS_TOUCH_DEVICE) {
      joystickBase = document.createElement("div");
      joystickBase.className = "joystick-base";
      joystickKnob = document.createElement("div");
      joystickKnob.className = "joystick-knob";
      joystickBase.appendChild(joystickKnob);

      mobileActions = document.createElement("div");
      mobileActions.className = "mobile-actions";
      mobileBtnB = document.createElement("button");
      mobileBtnB.className = "mobile-btn mobile-btn-b";
      mobileBtnB.textContent = "B";
      mobileBtnA = document.createElement("button");
      mobileBtnA.className = "mobile-btn mobile-btn-a";
      mobileBtnA.textContent = "A";
      mobileActions.appendChild(mobileBtnB);
      mobileActions.appendChild(mobileBtnA);

      overlayEls.push(joystickBase, mobileActions);
      attachMobileControlEvents();
    }
  }

  function attachOverlays() { overlayEls.forEach((el) => document.body.appendChild(el)); }
  function detachOverlays() { overlayEls.forEach((el) => el.remove()); }

  function showBirdseyeHud() {
    crosshairElement.style.display = "block";
    birdseyeHudElement.style.display = "block";
  }
  function hideBirdseyeHud() {
    crosshairElement.style.display = "none";
    birdseyeHudElement.style.display = "none";
  }

  /** Both directions cut the camera/HUD over instantly and play the hat
   * animation alongside it, rather than gating on the animation - only
   * entry gates on it (see birdseyeEntryPending below), since watching the
   * throw wind-up in third person before the view actually changes is the
   * whole point, whereas there's nothing to gain by delaying the return to
   * normal view just to watch the catch play out from birdseye's zoomed-out
   * camera. The hat reappears the instant Catch starts (it's already
   * falling back towards the player's hands as the animation begins), the
   * mirror image of Throw only hiding it once the wind-up actually
   * finishes - see enterBirdseyeAfterThrow(). */
  function toggleBirdseye() {
    if (isCatching) return; // already mid catch-back - ignore repeat presses
    if (birdseyeMode) {
      birdseyeMode = false;
      hideBirdseyeHud();
      if (hatArmature) hatArmature.visible = true;
      playCatchAnimation();
      return;
    }
    if (birdseyeEntryPending) return; // already winding up - ignore repeat presses
    birdseyeEntryPending = true;
    playThrowAnimation();
  }

  /** Called from the mixer "finished" listener once the Throw clip
   * completes - the actual moment birdseye mode kicks in, see
   * toggleBirdseye(). */
  function enterBirdseyeAfterThrow() {
    birdseyeEntryPending = false;
    birdseyeMode = true;
    showBirdseyeHud();
    if (hatArmature) hatArmature.visible = false; // it's been thrown - comes back the instant Catch starts, see toggleBirdseye()
    birdseyeState.position.copy(playerState.position);
    birdseyeState.forward.copy(playerState.forward);
    sfx.playZoomOut();
  }

  /* ---------------- models / materials ---------------- */
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath("/draco/");
  // Without this, the decoder wasm is only fetched lazily on first use -
  // i.e. after a model's JSON has already downloaded and parsing has
  // started - adding a fully serial round-trip to every load. Preloading
  // kicks that fetch off immediately, overlapping it with the GLB downloads
  // instead of trailing behind them.
  dracoLoader.preload();
  const gltfLoader = new GLTFLoader();
  gltfLoader.setDRACOLoader(dracoLoader);
  gltfLoader.manager.onError = (url) => {
    console.error(`[assets] failed to load "${url}" - check the path is correct and the file is actually being served.`);
  };

  function logMaterialInfo(root, label) {
    root.traverse((object) => {
      if (!object.isMesh) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((mat) => {
        if (!mat) return;
        const color = mat.color ? `#${mat.color.getHexString()}` : "n/a";
        console.log(`[materials:${label}] "${object.name || "(unnamed)"}" - color: ${color}, has texture map: ${!!mat.map}`);
      });
    });
  }

  let mixer = null, model = null, hatArmature = null, coconutProp = null;
  let idleAction = null, walkAction = null, jumpAction = null, throwAction = null, catchAction = null, drinkAction = null, currentAction = null;
  const animationFadeDuration = 0.2;
  // Set while the one-shot Throw/Catch/Drink clips are playing, so
  // updateAnimationState() leaves them alone instead of immediately fading
  // back to idle/walk on the very next frame - same idea as the isGrounded
  // guard below does for the jump clip, just driven by the mixer's own
  // "finished" event instead of a physical state. The mixer only updates in
  // mode "play" (see update()), so if a round ends mid-clip that event never
  // fires - resetRound()/hideGameplayActors() must also force these back to
  // false, or updateAnimationState() gets stuck skipping walk/idle forever.
  let isThrowing = false;
  let isCatching = false;
  let isDrinking = false;

  function playAnimation(action) {
    if (!action || action === currentAction) return;
    if (currentAction) currentAction.fadeOut(animationFadeDuration);
    action.reset().fadeIn(animationFadeDuration).play();
    currentAction = action;
  }
  /** A genuine jump input should always restart the clip from the top -
   * even a double jump while jumpAction is already playing/held - so this
   * bypasses playAnimation()'s "already current" no-op guard. */
  function playJumpAnimation() {
    if (!jumpAction) return;
    if (currentAction && currentAction !== jumpAction) currentAction.fadeOut(animationFadeDuration);
    jumpAction.reset().fadeIn(animationFadeDuration).play();
    currentAction = jumpAction;
  }
  /** Restarts the Throw clip from the top every time (mirrors
   * playJumpAnimation()) - triggered once per birdseye entry, see
   * toggleBirdseye(). */
  function playThrowAnimation() {
    if (!throwAction) return;
    if (currentAction && currentAction !== throwAction) currentAction.fadeOut(animationFadeDuration);
    throwAction.reset().fadeIn(animationFadeDuration).play();
    currentAction = throwAction;
    isThrowing = true;
  }
  /** Restarts the Catch clip from the top every time (mirrors
   * playThrowAnimation()) - triggered once per birdseye exit, see
   * toggleBirdseye(). */
  function playCatchAnimation() {
    if (!catchAction) return;
    if (currentAction && currentAction !== catchAction) currentAction.fadeOut(animationFadeDuration);
    catchAction.reset().fadeIn(animationFadeDuration).play();
    currentAction = catchAction;
    isCatching = true;
  }
  /** Restarts the Drink clip from the top every time (mirrors
   * playThrowAnimation()/playCatchAnimation()) - triggered on picking up
   * coconut water, see updatePickups(). The coconut prop that's baked into
   * this clip (a "Coco_Water" mesh sitting alongside "Player" in the same
   * file, not attached to any bone) only exists to be held during this one
   * animation, so it's hidden the rest of the time - see coconutProp. */
  function playDrinkAnimation() {
    if (!drinkAction) return;
    if (currentAction && currentAction !== drinkAction) currentAction.fadeOut(animationFadeDuration);
    drinkAction.reset().fadeIn(animationFadeDuration).play();
    currentAction = drinkAction;
    isDrinking = true;
    if (coconutProp) coconutProp.visible = true;
  }
  function updateAnimationState() {
    if (!idleAction || !walkAction || !jumpAction) return;
    if (isThrowing || isCatching || isDrinking) return;
    if (!playerState.isGrounded) { playAnimation(jumpAction); sfx.setFootstepsPlaying(false); return; }
    const isMoving = keysPressed["w"] || keysPressed["s"] || keysPressed["arrowup"] || keysPressed["arrowdown"] || Math.abs(joystickVector.y) > 0.05;
    playAnimation(isMoving ? walkAction : idleAction);
    // Birdseye/trivia don't actually move the player (updatePlayerMovement
    // is skipped for both), so footsteps would otherwise play under a
    // standing-still player if a movement key happened to still be held.
    sfx.setFootstepsPlaying(isMoving && !birdseyeMode && !isTriviaModalOpen);
  }

  const playerModelPath = "/models/Characters/Player/player.glb";
  const hiderModelPath = "/models/Characters/Bliss/bliss.glb";

  let bushMaterial, gateMaterial;
  const earthMaterial = new THREE.MeshBasicMaterial({ color: 0x7a5a3a });
  const platformMaterial = earthMaterial;

  /* ---------------- collision ---------------- */
  let collisionMeshData = [];
  // World-space wall triangles bucketed by nav-grid cell - built once in
  // buildNavigationGrid() (which needs this same bucketing for its
  // raycasts anyway) and reused here so per-frame player collision only
  // tests the handful of triangles near the player instead of every wall
  // triangle in the maze. See resolveStaticWallCollision().
  let wallCollisionIndex = null;

  const _cptTmp1 = new THREE.Vector3();
  const _cptTmp2 = new THREE.Vector3();
  const _cptTmp3 = new THREE.Vector3();
  const _closestPoint = new THREE.Vector3();
  const _bestPoint = new THREE.Vector3();
  const _wallDirTmp = new THREE.Vector3();
  const _wallSeenTris = new Set();

  function closestPointOnTriangle(p, a, b, c, target) {
    const ab = _cptTmp1.subVectors(b, a);
    const ac = _cptTmp2.subVectors(c, a);
    const ap = _cptTmp3.subVectors(p, a);

    const d1 = ab.dot(ap);
    const d2 = ac.dot(ap);
    if (d1 <= 0 && d2 <= 0) return target.copy(a);

    const bp = _cptTmp3.subVectors(p, b);
    const d3 = ab.dot(bp);
    const d4 = ac.dot(bp);
    if (d3 >= 0 && d4 <= d3) return target.copy(b);

    const vc = d1 * d4 - d3 * d2;
    if (vc <= 0 && d1 >= 0 && d3 <= 0) {
      const v = d1 / (d1 - d3);
      return target.copy(a).addScaledVector(ab, v);
    }

    const cp = _cptTmp3.subVectors(p, c);
    const d5 = ab.dot(cp);
    const d6 = ac.dot(cp);
    if (d6 >= 0 && d5 <= d6) return target.copy(c);

    const vb = d5 * d2 - d1 * d6;
    if (vb <= 0 && d2 >= 0 && d6 <= 0) {
      const w = d2 / (d2 - d6);
      return target.copy(a).addScaledVector(ac, w);
    }

    const va = d3 * d6 - d5 * d4;
    if (va <= 0 && (d4 - d3) >= 0 && (d5 - d6) >= 0) {
      const w = (d4 - d3) / ((d4 - d3) + (d5 - d6));
      const bc = _cptTmp1.subVectors(c, b);
      return target.copy(b).addScaledVector(bc, w);
    }

    const denom = 1 / (va + vb + vc);
    const v = vb * denom;
    const w = vc * denom;
    return target.copy(a).addScaledVector(ab, v).addScaledVector(ac, w);
  }

  function extractTriangles(geometry) {
    const positionAttr = geometry.attributes.position;
    const index = geometry.index;
    const triCount = index ? index.count / 3 : positionAttr.count / 3;
    const triangles = [];
    for (let i = 0; i < triCount; i++) {
      let ia, ib, ic;
      if (index) {
        ia = index.getX(i * 3); ib = index.getX(i * 3 + 1); ic = index.getX(i * 3 + 2);
      } else {
        ia = i * 3; ib = i * 3 + 1; ic = i * 3 + 2;
      }
      triangles.push({
        a: new THREE.Vector3().fromBufferAttribute(positionAttr, ia),
        b: new THREE.Vector3().fromBufferAttribute(positionAttr, ib),
        c: new THREE.Vector3().fromBufferAttribute(positionAttr, ic),
      });
    }
    return triangles;
  }

  function buildCollisionMeshData(root) {
    const data = [];
    root.traverse((object) => {
      if (object.isInstancedMesh) {
        const baseTriangles = extractTriangles(object.geometry);
        object.updateWorldMatrix(true, false);
        const instanceMatrix = new THREE.Matrix4();
        for (let i = 0; i < object.count; i++) {
          object.getMatrixAt(i, instanceMatrix);
          const worldMatrix = object.matrixWorld.clone().multiply(instanceMatrix);
          data.push({ matrixWorld: worldMatrix, invMatrixWorld: worldMatrix.clone().invert(), triangles: baseTriangles });
        }
      } else if (object.isMesh && object.geometry?.attributes?.position) {
        object.updateWorldMatrix(true, false);
        data.push({
          matrixWorld: object.matrixWorld.clone(),
          invMatrixWorld: object.matrixWorld.clone().invert(),
          triangles: extractTriangles(object.geometry),
        });
      }
    });
    return data;
  }

  function getActiveGateColliders() {
    const gateColliders = [];
    for (const gate of gates) {
      if (!gate.opened) gateColliders.push(...gate.colliderData);
    }
    return gateColliders;
  }

  // Gates are few (a handful of small meshes) and dynamic (open/closed), so
  // they keep the original per-collider local-space closest-point loop -
  // it's cheap at this scale and simplest to keep correct while doors move.
  function resolveGateCollisions(resolved, gateColliders) {
    for (const collider of gateColliders) {
      const localPos = resolved.clone().applyMatrix4(collider.invMatrixWorld);
      const scale = new THREE.Vector3().setFromMatrixScale(collider.matrixWorld);
      const avgScale = (scale.x + scale.y + scale.z) / 3 || 1;
      const localRadius = playerCollisionRadius / avgScale;

      let closestDistSq = Infinity;
      let foundClosest = false;
      for (const tri of collider.triangles) {
        closestPointOnTriangle(localPos, tri.a, tri.b, tri.c, _closestPoint);
        const distSq = _closestPoint.distanceToSquared(localPos);
        if (distSq < closestDistSq) { closestDistSq = distSq; foundClosest = true; _bestPoint.copy(_closestPoint); }
      }

      if (foundClosest && closestDistSq < localRadius * localRadius) {
        const dist = Math.sqrt(closestDistSq);
        const pushDir = dist > 1e-6 ? localPos.clone().sub(_bestPoint).normalize() : new THREE.Vector3(0, 1, 0);
        localPos.copy(_bestPoint).addScaledVector(pushDir, localRadius);
        resolved.copy(localPos.applyMatrix4(collider.matrixWorld));
      }
    }
  }

  // The static maze walls are the expensive part (thousands of triangles),
  // so instead of testing every one every frame, only test triangles in
  // the nav-grid cell the player is in plus its 8 neighbours - using the
  // same spatial buckets buildNavigationGrid() already builds for its
  // raycasts. Triangles here are pre-baked to world space, so no
  // per-collider local-space transform is needed either.
  // A single nearest-triangle push-out resolves a flat wall fine, but at a
  // convex corner (e.g. where a bush hedge ends) pushing away from only the
  // nearest of the two adjoining faces can still leave the point embedded
  // in the other one. That's exactly the shape of the dash-through-bush
  // bug: the dash's larger one-shot steps hit a hedge corner, one pass of
  // this push-out isn't enough to fully evict the point, and there's no
  // follow-up frame (as normal walking has) to finish the job. Repeating
  // the nearest-triangle search a few times, letting each pass react to
  // whichever face is now closest, converges the point outside both -
  // standard trick for approximating multi-face corner collision without a
  // full swept/continuous collision system.
  const WALL_COLLISION_ITERATIONS = 4;
  function resolveStaticWallCollision(resolved) {
    if (!wallCollisionIndex) return;
    const { triangles, buckets } = wallCollisionIndex;

    for (let iter = 0; iter < WALL_COLLISION_ITERATIONS; iter++) {
      const cell = directionToNavCell(_wallDirTmp.copy(resolved).normalize());

      let closestDistSq = playerCollisionRadius * playerCollisionRadius;
      let found = false;
      _wallSeenTris.clear();
      for (let dgy = -1; dgy <= 1; dgy++) {
        const ny = cell.gy + dgy;
        if (ny < 0 || ny >= navGridHeight) continue;
        for (let dgx = -1; dgx <= 1; dgx++) {
          const nx = ((cell.gx + dgx) % navGridWidth + navGridWidth) % navGridWidth;
          const bucket = buckets.get(ny * navGridWidth + nx);
          if (!bucket) continue;
          for (const triIndex of bucket) {
            if (_wallSeenTris.has(triIndex)) continue;
            _wallSeenTris.add(triIndex);
            const tri = triangles[triIndex];
            closestPointOnTriangle(resolved, tri.a, tri.b, tri.c, _closestPoint);
            const distSq = _closestPoint.distanceToSquared(resolved);
            if (distSq < closestDistSq) { closestDistSq = distSq; found = true; _bestPoint.copy(_closestPoint); }
          }
        }
      }

      if (!found) break;
      const dist = Math.sqrt(closestDistSq);
      const pushDir = dist > 1e-6 ? resolved.clone().sub(_bestPoint).normalize() : new THREE.Vector3(0, 1, 0);
      resolved.copy(_bestPoint).addScaledVector(pushDir, playerCollisionRadius);
    }
  }

  function resolveWallCollisions(position) {
    const resolved = position.clone();
    resolveStaticWallCollision(resolved);
    const gateColliders = getActiveGateColliders();
    if (gateColliders.length > 0) resolveGateCollisions(resolved, gateColliders);
    return projectToSurface(resolved);
  }

  /* ---------------- terrain height map ---------------- */
  /**
   * Which meshes count as "ground" for height-sampling purposes. We try
   * the explicit Blender object names first ("Earth"/"SpawnPlat"); if
   * neither is found we fall back to a name-based scan of the whole GLTF
   * so a flat-top plateau (or any other terrain feature) named something
   * else still ends up in the height map and gets used for surface
   * placement + wall collision, instead of silently being ignored and
   * treated as if the earth were a perfect sphere there.
   */
  function findGroundObjects(root, earth, spawnPlat) {
    const explicit = [earth, spawnPlat].filter(Boolean);
    if (explicit.length > 0) return explicit;

    const fallback = [];
    root.traverse((obj) => {
      if (!obj.isMesh) return;
      const name = (obj.name || "").toLowerCase();
      if (/earth|ground|terrain|plat|floor|top/.test(name) && !/collision|visual|gate/.test(name)) {
        fallback.push(obj);
      }
    });
    if (fallback.length === 0) {
      console.warn(
        '[maze] could not find "Earth"/"SpawnPlat" objects, and the name-based fallback found nothing ground-like either - ' +
        "terrain height (including any flat top) won't be captured, and the player will walk on a perfect sphere instead."
      );
    } else {
      console.log(`[maze] terrain height map built from fallback-matched objects: ${fallback.map((o) => o.name).join(", ")}`);
    }
    return fallback;
  }

  function buildTerrainHeightMap(groundObjects) {
    const heightMap = new Float32Array(terrainGridWidth * terrainGridHeight);
    const sampled = new Uint8Array(terrainGridWidth * terrainGridHeight);
    const worldPos = new THREE.Vector3();

    for (const obj of groundObjects) {
      if (!obj) continue;
      obj.updateWorldMatrix(true, true);
      obj.traverse((object) => {
        if (!object.isMesh) return;
        const positions = object.geometry.attributes.position;
        for (let i = 0; i < positions.count; i++) {
          worldPos.fromBufferAttribute(positions, i);
          object.localToWorld(worldPos);
          const radius = worldPos.length();
          const theta = (Math.atan2(worldPos.z, worldPos.x) + Math.PI * 2) % (Math.PI * 2);
          const phi = Math.acos(THREE.MathUtils.clamp(worldPos.y / radius, -1, 1));
          const ix = Math.floor((theta / (Math.PI * 2)) * terrainGridWidth) % terrainGridWidth;
          const iy = THREE.MathUtils.clamp(Math.floor((phi / Math.PI) * terrainGridHeight), 0, terrainGridHeight - 1);
          const index = iy * terrainGridWidth + ix;
          if (!sampled[index] || radius > heightMap[index]) { heightMap[index] = radius; sampled[index] = 1; }
        }
      });
    }
    fillTerrainHeightMapGaps(heightMap, sampled);
    terrainHeightMap = heightMap;
  }

  function fillTerrainHeightMapGaps(heightMap, sampled) {
    let remaining = 0;
    for (let i = 0; i < sampled.length; i++) if (!sampled[i]) remaining++;
    let guard = 0;
    const maxPasses = terrainGridWidth + terrainGridHeight;
    while (remaining > 0 && guard < maxPasses) {
      guard++;
      const newlyFilled = [];
      for (let y = 0; y < terrainGridHeight; y++) {
        for (let x = 0; x < terrainGridWidth; x++) {
          const index = y * terrainGridWidth + x;
          if (sampled[index]) continue;
          let total = 0, count = 0;
          for (let dy = -1; dy <= 1; dy++) {
            const ny = y + dy;
            if (ny < 0 || ny >= terrainGridHeight) continue;
            for (let dx = -1; dx <= 1; dx++) {
              const nx = (x + dx + terrainGridWidth) % terrainGridWidth;
              const nIndex = ny * terrainGridWidth + nx;
              if (sampled[nIndex]) { total += heightMap[nIndex]; count++; }
            }
          }
          if (count > 0) newlyFilled.push([index, total / count]);
        }
      }
      if (newlyFilled.length === 0) break;
      for (const [index, value] of newlyFilled) { heightMap[index] = value; sampled[index] = 1; }
      remaining -= newlyFilled.length;
    }
  }

  function conformSphereWorldToTerrain() {
    playerState.position.copy(projectToSurface(playerState.position));
    playerState.forward.copy(computeInitialForward(playerState.position));
    if (model && camera) {
      camera.position.copy(getChaseCameraTarget());
      camera.up.copy(getUp(playerState.position));
      camera.lookAt(getLookAtTarget());
    }
  }

  /* ---------------- trivia questions ---------------- */
  let triviaQuestions = [];

  async function loadTriviaQuestions() {
    try {
      const response = await fetch("/trivia.json");
      if (!response.ok) throw new Error("trivia.json not found");
      const data = await response.json();
      if (!Array.isArray(data)) {
        console.warn("[trivia] trivia.json is not an array - using empty question list.");
        return;
      }
      triviaQuestions = data.filter(
        (q) => q.prompt && Array.isArray(q.options) && q.options.length >= 2 && q.options[q.correctIndex] !== undefined
      );
      if (triviaQuestions.length === 0) console.warn("[trivia] no valid questions found in trivia.json - gates will have no questions.");
      else console.log(`[trivia] loaded ${triviaQuestions.length} predefined questions.`);
    } catch (err) {
      console.error("[trivia] failed to load trivia.json - gates will have no questions.", err);
    }
  }

  function shuffled(array) {
    const result = array.slice();
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  /** Picks `count` questions at random, drawing from a shuffled pool that
   * gets reshuffled once exhausted - there are more questions than gates,
   * so most rounds won't repeat a question, and which ones come up varies
   * game to game. */
  function pickRandomQuestions(count) {
    if (triviaQuestions.length === 0) {
      return Array.from({ length: count }, () => ({ prompt: "Question missing", options: ["-", "-", "-"], correctAnswer: "-" }));
    }
    const picks = [];
    let pool = [];
    while (picks.length < count) {
      if (pool.length === 0) pool = shuffled(triviaQuestions);
      const q = pool.pop();
      picks.push({ prompt: q.prompt, options: q.options, correctAnswer: q.options[q.correctIndex] });
    }
    return picks;
  }

  /** (Re)assigns a random question to every gate - called once gates are
   * built and again at the start of each round, so both which questions
   * appear and which gate asks them change game to game. */
  function assignRandomQuestionsToGates() {
    const questions = pickRandomQuestions(gates.length);
    gates.forEach((gate, index) => { gate.question = questions[index]; });
  }

  /* ---------------- trivia gates ---------------- */
  function loadGatesFromMazeRoot(mazeRoot) {
    const gateGroups = mazeRoot.children.filter((child) => child.name.toLowerCase().startsWith("gate_"));
    if (gateGroups.length === 0) {
      console.warn("[gates] No objects starting with 'Gate_' found in Maze.glb - no trivia gates will be present.");
      return false;
    }

    const worldPosition = new THREE.Vector3();
    gateGroups.forEach((gateGroup, index) => {
      const doorChild = gateGroup.children.find((child) => child.name.toLowerCase().includes("door"));
      const frameChild = gateGroup.children.find((child) => child.name.toLowerCase().includes("frame"));
      if (!doorChild) {
        console.warn(`[gates] "${gateGroup.name}" has no child containing "door" - skipping.`);
        return;
      }

      doorChild.traverse((node) => {
        if (node.isMesh) { node.material = gateMaterial; node.castShadow = true; node.receiveShadow = true; }
      });
      if (frameChild) {
        frameChild.traverse((node) => {
          if (node.isMesh) { node.material = gateMaterial; node.castShadow = true; node.receiveShadow = true; }
        });
      }

      const collisionRoot = gateGroup.children.find((child) => child.name.toLowerCase().includes("collision"));
      let colliderData = [];
      if (collisionRoot) {
        collisionRoot.traverse((object) => { if (object.isMesh) object.visible = false; });
        colliderData = buildCollisionMeshData(collisionRoot);
        if (colliderData.length === 0) {
          console.warn(`[gates] "${gateGroup.name}/${collisionRoot.name}" produced no triangles - no collision.`);
        }
      } else {
        console.warn(`[gates] "${gateGroup.name}" has no child containing "collision" - it won't block the player.`);
      }

      gateGroup.getWorldPosition(worldPosition);
      const closedQuat = doorChild.quaternion.clone();
      const openQuat = closedQuat.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2));

      gates.push({
        id: gateGroup.name || `gate-${index}`,
        position: worldPosition.clone(),
        doorVisual: doorChild,
        frameVisual: frameChild || null,
        colliderData,
        opened: false,
        question: null,
        doorOpenProgress: 0,
        doorOpenSpeed: 1.5,
        doorClosedQuat: closedQuat,
        doorOpenQuat: openQuat,
      });
    });

    assignRandomQuestionsToGates();
    console.log(`[gates] loaded ${gates.length} gate(s) from Blender (prefixed with "Gate_").`);
    return gates.length > 0;
  }

  const gameParams = { durationSeconds: 30 };
  const gateParams = { interactRadius: 0.9, timeBonusSeconds: 10 };
  const gates = [];
  let activeGate = null;
  let isTriviaModalOpen = false;
  let currentGate = null;
  let isNearHider = false;

  /** Mirrors updateGateProximity() for the "close enough to catch Bliss"
   * prompt - kept as its own flag (rather than recomputing distance inside
   * tryCatchHider()) so handleActionButton() can check it too and skip
   * the jump on the same press that catches Bliss (see handleActionButton).
   * Computed independently of gate proximity (and before it - see the call
   * order in the main loop) so catching Bliss is never silently unavailable
   * just because a gate happens to be in range at the same spot. */
  function updateCatchProximity() {
    if (isTriviaModalOpen || !hiderPlaced || hasFound || hasTimedOut || birdseyeMode) {
      isNearHider = false;
      catchPromptElement.style.display = "none";
      return;
    }
    isNearHider = playerState.position.distanceTo(hiderState.position) <= catchRadius;
    catchPromptElement.style.display = isNearHider ? "block" : "none";
  }

  /** Catching Bliss takes priority over a nearby gate when both are in
   * range at once - relies on updateCatchProximity() having already run
   * this frame (see the call order in the main loop) so isNearHider is
   * fresh, not last frame's value. Without this, standing where a gate and
   * a catchable Bliss overlap would silently always open the gate instead
   * (see handleActionButton()), which reads as the game just not letting
   * you catch her. birdseyeMode gets the same treatment - marking a path
   * point takes priority over a gate happening to be in range underneath
   * where the player's stood while aiming. */
  function updateGateProximity() {
    if (isTriviaModalOpen || isNearHider || birdseyeMode) { activeGate = null; gatePromptElement.style.display = "none"; return; }
    let closestGate = null, closestDistance = Infinity;
    for (const gate of gates) {
      if (gate.opened) continue;
      const distance = playerState.position.distanceTo(gate.position);
      if (distance < gateParams.interactRadius && distance < closestDistance) { closestDistance = distance; closestGate = gate; }
    }
    activeGate = closestGate;
    gatePromptElement.style.display = activeGate ? "block" : "none";
  }

  function tryOpenNearbyGate() {
    if (!activeGate || activeGate.opened || isTriviaModalOpen) return;
    openTriviaModal(activeGate);
  }

  function openTriviaModal(gate) {
    currentGate = gate;
    isTriviaModalOpen = true;
    // Player movement (incl. gravity/height) freezes entirely while the
    // modal is open, so any leftover mid-air jump would otherwise just
    // resume - and visibly finish landing - the instant it closes. Landing
    // them here up front means there's nothing left to resume.
    playerState.height = 0;
    playerState.verticalVelocity = 0;
    playerState.isGrounded = true;
    playerState.jumpsUsed = 0;
    syncPlayerModelTransform();
    sfx.playTriviaOpen();
    triviaPrompt.textContent = gate.question.prompt;
    triviaOptionsContainer.innerHTML = "";

    gate.question.options.forEach((option, index) => {
      const button = document.createElement("button");
      button.className = "trivia-option-btn";
      button.textContent = option;
      button.addEventListener("click", () => answerTriviaOption(index));
      triviaOptionsContainer.appendChild(button);
    });

    triviaOverlay.style.display = "flex";
    gatePromptElement.style.display = "none";
    catchPromptElement.style.display = "none";
    hideMobileControls();
  }

  function closeTriviaModal() {
    isTriviaModalOpen = false;
    currentGate = null;
    triviaOverlay.style.display = "none";
    if (mode === "play") showMobileControls();
  }

  /** Answer feedback is just colour: the button you picked, plus (if you
   * picked wrong) the actual correct one - no explanatory text needed. */
  function answerTriviaOption(index) {
    if (!isTriviaModalOpen || !currentGate) return;
    const gate = currentGate;
    const selected = gate.question.options[index];
    if (selected === undefined) return;

    const isCorrect = selected === gate.question.correctAnswer;
    gate.opened = true;

    const buttons = Array.from(triviaOptionsContainer.children);
    buttons.forEach((button, buttonIndex) => {
      button.disabled = true;
      if (buttonIndex === index) button.classList.add(isCorrect ? "correct" : "incorrect");
      else if (!isCorrect && button.textContent === gate.question.correctAnswer) button.classList.add("correct");
    });

    if (isCorrect) {
      timeRemaining += gateParams.timeBonusSeconds;
      flashCountdownBonus(gateParams.timeBonusSeconds);
      sfx.playTriviaCorrect();
    } else {
      sfx.playTriviaWrong();
    }
    setTimeout(closeTriviaModal, isCorrect ? 700 : 1500);
  }

  /* ---------------- coconut water pickups ---------------- */
  const pickupModelPath = "/models/Pickups/coco_water.gltf";
  const pickupParams = { count: 5, interactRadius: 0.6, timeBonusSeconds: 5, modelScale: 0.1 };
  const pickups = [];
  const PICKUP_HOVER_HEIGHT = 0.03;
  const PICKUP_SPIN_SPEED = 1.4;

  /** Clones the loaded Coco_Water model once per pickup slot - called from
   * loadAssets() the moment the model finishes loading (see
   * pickupLoadPromise), mirroring how spawnHider() reacts to the hider
   * model's own load promise. Plain Object3D.clone(true) is enough here
   * (no skinning/bones on this model, unlike the player/hider). */
  function spawnPickupModels(gltf) {
    for (let i = 0; i < pickupParams.count; i++) {
      const mesh = gltf.scene.clone(true);
      mesh.scale.setScalar(pickupParams.modelScale);
      mesh.traverse((object) => { if (object.isMesh) object.castShadow = true; });
      mesh.visible = false;
      scene.add(mesh);
      pickups.push({ mesh, position: new THREE.Vector3(), cellKey: -1 });
    }
  }

  /** Places a single pickup on a fresh random walkable cell, avoiding
   * whichever cells are already in usedKeys (the other currently-active
   * pickups) so two of them never land on top of each other - used both
   * for the initial per-round scatter (spawnPickups()) and to instantly
   * relocate one the moment it's collected (updatePickups()), which is
   * what keeps pickupParams.count of them out on the map at all times
   * instead of the pool draining over the round. */
  function placePickupAtRandomCell(pickup, usedKeys) {
    let cell = pickRandomOpenCell();
    for (let attempt = 0; attempt < 8; attempt++) {
      const key = cell.gy * navGridWidth + cell.gx;
      if (!usedKeys.has(key)) break;
      cell = pickRandomOpenCell();
    }
    pickup.cellKey = cell.gy * navGridWidth + cell.gx;
    usedKeys.add(pickup.cellKey);

    const direction = getCenteredDirectionForCell(cell.gx, cell.gy);
    pickup.position.copy(direction).multiplyScalar(getSurfaceRadius(direction) + PICKUP_HOVER_HEIGHT);
    pickup.mesh.position.copy(pickup.position);
    pickup.mesh.up.copy(getUp(pickup.position));
    pickup.mesh.lookAt(pickup.position.clone().add(computeInitialForward(pickup.position)));
    pickup.mesh.visible = true;
  }

  /** (Re)spawns every pickup on a fresh random walkable cell - called once
   * per round from resetRound(). */
  function spawnPickups() {
    if (!navGrid) return;
    const usedKeys = new Set();
    for (const pickup of pickups) placePickupAtRandomCell(pickup, usedKeys);
  }

  function hidePickups() {
    for (const pickup of pickups) pickup.mesh.visible = false;
  }

  function updatePickups(deltaTime) {
    for (const pickup of pickups) {
      pickup.mesh.rotation.y += deltaTime * PICKUP_SPIN_SPEED;
      if (playerState.position.distanceTo(pickup.position) <= pickupParams.interactRadius) {
        timeRemaining += pickupParams.timeBonusSeconds;
        flashCountdownBonus(pickupParams.timeBonusSeconds);
        playDrinkAnimation();
        const usedKeys = new Set(pickups.filter((p) => p !== pickup).map((p) => p.cellKey));
        placePickupAtRandomCell(pickup, usedKeys);
      }
    }
  }

  /* ---------------- navigation grid + pathfinding ---------------- */
  const navGridWidth = 140;
  const navGridHeight = 70;
  let navGrid = null;

  const mazePolarClearanceDeg = 25;
  const mazePolarClearanceRadians = THREE.MathUtils.degToRad(mazePolarClearanceDeg);

  function directionToNavCell(direction) {
    const theta = (Math.atan2(direction.z, direction.x) + Math.PI * 2) % (Math.PI * 2);
    const phi = Math.acos(THREE.MathUtils.clamp(direction.y, -1, 1));
    const gx = THREE.MathUtils.clamp(Math.floor((theta / (Math.PI * 2)) * navGridWidth), 0, navGridWidth - 1);
    const gy = THREE.MathUtils.clamp(Math.floor((phi / Math.PI) * navGridHeight), 0, navGridHeight - 1);
    return { gx, gy };
  }

  function navCellToDirection(gx, gy) {
    const theta = ((gx + 0.5) / navGridWidth) * Math.PI * 2;
    const phi = ((gy + 0.5) / navGridHeight) * Math.PI;
    return new THREE.Vector3(Math.sin(phi) * Math.cos(theta), Math.cos(phi), Math.sin(phi) * Math.sin(theta));
  }

  function getNavGridNeighbors(gx, gy) {
    const neighbors = [];
    const offsets = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const [dx, dy] of offsets) {
      const ny = gy + dy;
      if (ny < 0 || ny >= navGridHeight) continue;
      const nx = ((gx + dx) % navGridWidth + navGridWidth) % navGridWidth;
      neighbors.push({ gx: nx, gy: ny });
    }
    return neighbors;
  }

  function collectWorldSpaceWallTriangles() {
    const triangles = [];
    for (const collider of collisionMeshData) {
      for (const tri of collider.triangles) {
        triangles.push({
          a: tri.a.clone().applyMatrix4(collider.matrixWorld),
          b: tri.b.clone().applyMatrix4(collider.matrixWorld),
          c: tri.c.clone().applyMatrix4(collider.matrixWorld),
        });
      }
    }
    return triangles;
  }

  function buildWallTriangleBuckets(triangles) {
    const buckets = new Map();
    const addToBucket = (gx, gy, index) => {
      const key = gy * navGridWidth + gx;
      let bucket = buckets.get(key);
      if (!bucket) { bucket = []; buckets.set(key, bucket); }
      bucket.push(index);
    };
    const _dir = new THREE.Vector3();
    triangles.forEach((tri, index) => {
      const cells = [tri.a, tri.b, tri.c].map((v) => directionToNavCell(_dir.copy(v).normalize()));
      const minGx = Math.min(...cells.map((c) => c.gx)) - 1;
      const maxGx = Math.max(...cells.map((c) => c.gx)) + 1;
      const minGy = Math.max(0, Math.min(...cells.map((c) => c.gy)) - 1);
      const maxGy = Math.min(navGridHeight - 1, Math.max(...cells.map((c) => c.gy)) + 1);
      for (let gy = minGy; gy <= maxGy; gy++) {
        for (let gx = minGx; gx <= maxGx; gx++) {
          const wrappedGx = ((gx % navGridWidth) + navGridWidth) % navGridWidth;
          addToBucket(wrappedGx, gy, index);
        }
      }
    });
    return buckets;
  }

  function buildClearanceField(walkable) {
    const clearance = new Uint16Array(navGridWidth * navGridHeight);
    const visited = new Uint8Array(navGridWidth * navGridHeight);
    const queue = [];
    for (let key = 0; key < walkable.length; key++) {
      if (!walkable[key]) { visited[key] = 1; queue.push({ gx: key % navGridWidth, gy: Math.floor(key / navGridWidth) }); }
    }
    let head = 0;
    while (head < queue.length) {
      const cell = queue[head++];
      const key = cell.gy * navGridWidth + cell.gx;
      for (const neighbor of getNavGridNeighbors(cell.gx, cell.gy)) {
        const nKey = neighbor.gy * navGridWidth + neighbor.gx;
        if (visited[nKey]) continue;
        visited[nKey] = 1;
        clearance[nKey] = clearance[key] + 1;
        queue.push(neighbor);
      }
    }
    return clearance;
  }

  function buildNavigationGrid() {
    const wallTriangles = collectWorldSpaceWallTriangles();
    if (wallTriangles.length === 0) {
      console.warn("[nav] no wall triangles available - skipping navigation grid build.");
      return;
    }

    const buckets = buildWallTriangleBuckets(wallTriangles);
    wallCollisionIndex = { triangles: wallTriangles, buckets };
    const walkable = new Uint8Array(navGridWidth * navGridHeight);
    const rayStartRadius = sphereRadius + 10;
    const ray = new THREE.Ray();
    const hitPoint = new THREE.Vector3();

    for (let gy = 0; gy < navGridHeight; gy++) {
      for (let gx = 0; gx < navGridWidth; gx++) {
        const direction = navCellToDirection(gx, gy);
        ray.origin.copy(direction).multiplyScalar(rayStartRadius);
        ray.direction.copy(direction).multiplyScalar(-1);
        const key = gy * navGridWidth + gx;
        const candidates = buckets.get(key);
        let blocked = false;
        if (candidates) {
          for (const index of candidates) {
            const tri = wallTriangles[index];
            if (ray.intersectTriangle(tri.a, tri.b, tri.c, false, hitPoint)) { blocked = true; break; }
          }
        }
        walkable[key] = blocked ? 0 : 1;
      }
    }

    const clearance = buildClearanceField(walkable);
    const openCellKeys = [];
    const mazeOpenCellKeys = [];
    for (let key = 0; key < walkable.length; key++) {
      if (!walkable[key]) continue;
      openCellKeys.push(key);
      const gy = Math.floor(key / navGridWidth);
      const phi = ((gy + 0.5) / navGridHeight) * Math.PI;
      if (phi >= mazePolarClearanceRadians && phi <= Math.PI - mazePolarClearanceRadians) mazeOpenCellKeys.push(key);
    }
    if (mazeOpenCellKeys.length === 0) {
      console.warn("[nav] mazePolarClearanceDeg excluded every open cell - falling back to the full grid for hider placement.");
    }

    navGrid = { width: navGridWidth, height: navGridHeight, walkable, clearance, openCellKeys, mazeOpenCellKeys };
    const openCount = walkable.reduce((sum, v) => sum + v, 0);
    console.log(`[nav] built ${navGridWidth}x${navGridHeight} grid (${openCount}/${walkable.length} open cells) from ${wallTriangles.length} wall triangles.`);
    tryInitHider();
  }

  const centeringSearchRadius = 4;

  function findNearestWalkableCell(gx, gy) {
    if (!navGrid) return { gx, gy };
    const { walkable, clearance } = navGrid;
    const startKey = gy * navGridWidth + gx;

    let anchor = { gx, gy };
    if (!walkable[startKey]) {
      const visited = new Set([startKey]);
      const queue = [{ gx, gy }];
      let head = 0, found = null;
      while (head < queue.length && !found) {
        const cell = queue[head++];
        for (const neighbor of getNavGridNeighbors(cell.gx, cell.gy)) {
          const key = neighbor.gy * navGridWidth + neighbor.gx;
          if (visited.has(key)) continue;
          visited.add(key);
          if (walkable[key]) { found = neighbor; break; }
          queue.push(neighbor);
        }
      }
      if (!found) return { gx, gy };
      anchor = found;
    }

    const anchorKey = anchor.gy * navGridWidth + anchor.gx;
    let best = { gx: anchor.gx, gy: anchor.gy, score: clearance[anchorKey] };
    const visited = new Set([anchorKey]);
    const queue = [{ gx: anchor.gx, gy: anchor.gy, depth: 0 }];
    let head = 0;
    while (head < queue.length) {
      const cell = queue[head++];
      if (cell.depth >= centeringSearchRadius) continue;
      for (const neighbor of getNavGridNeighbors(cell.gx, cell.gy)) {
        const key = neighbor.gy * navGridWidth + neighbor.gx;
        if (visited.has(key) || !walkable[key]) continue;
        visited.add(key);
        const score = clearance[key];
        if (score > best.score) best = { gx: neighbor.gx, gy: neighbor.gy, score };
        queue.push({ gx: neighbor.gx, gy: neighbor.gy, depth: cell.depth + 1 });
      }
    }
    return { gx: best.gx, gy: best.gy };
  }

  const pathCenteringWeight = 3;

  function findPath(startCell, targetCell) {
    if (!navGrid) return [];
    const { walkable, clearance } = navGrid;
    const keyOf = (c) => c.gy * navGridWidth + c.gx;
    const startKey = keyOf(startCell);
    const targetKey = keyOf(targetCell);
    if (!walkable[startKey] || !walkable[targetKey]) return [];
    if (startKey === targetKey) return [startCell];

    const heuristic = (a, b) => {
      const dxRaw = Math.abs(a.gx - b.gx);
      const dx = Math.min(dxRaw, navGridWidth - dxRaw);
      const dy = Math.abs(a.gy - b.gy);
      return dx + dy;
    };

    const open = new Map([[startKey, { ...startCell, f: heuristic(startCell, targetCell) }]]);
    const cameFrom = new Map();
    const gScore = new Map([[startKey, 0]]);

    while (open.size > 0) {
      let currentKey = null, current = null, bestF = Infinity;
      for (const [key, cell] of open) {
        if (cell.f < bestF) { bestF = cell.f; currentKey = key; current = cell; }
      }

      if (currentKey === targetKey) {
        const path = [{ gx: current.gx, gy: current.gy }];
        let key = currentKey;
        while (cameFrom.has(key)) {
          key = cameFrom.get(key);
          path.unshift({ gx: key % navGridWidth, gy: Math.floor(key / navGridWidth) });
        }
        return path;
      }

      open.delete(currentKey);
      for (const neighbor of getNavGridNeighbors(current.gx, current.gy)) {
        const neighborKey = keyOf(neighbor);
        if (!walkable[neighborKey]) continue;
        const stepCost = 1 + pathCenteringWeight / (clearance[neighborKey] + 1);
        const tentativeG = gScore.get(currentKey) + stepCost;
        if (tentativeG < (gScore.get(neighborKey) ?? Infinity)) {
          cameFrom.set(neighborKey, currentKey);
          gScore.set(neighborKey, tentativeG);
          open.set(neighborKey, { gx: neighbor.gx, gy: neighbor.gy, f: tentativeG + heuristic(neighbor, targetCell) });
        }
      }
    }
    return [];
  }

  let pathTrail = null;
  let clouds = null;

  function getCenteredDirectionForCell(gx, gy) {
    const { walkable, clearance } = navGrid;
    const key = gy * navGridWidth + gx;
    const selfWeight = clearance[key] + 1;
    const blended = navCellToDirection(gx, gy).multiplyScalar(selfWeight);
    let totalWeight = selfWeight;
    for (const neighbor of getNavGridNeighbors(gx, gy)) {
      const nKey = neighbor.gy * navGridWidth + neighbor.gx;
      if (!walkable[nKey]) continue;
      const weight = clearance[nKey] + 1;
      blended.addScaledVector(navCellToDirection(neighbor.gx, neighbor.gy), weight);
      totalWeight += weight;
    }
    return blended.multiplyScalar(1 / totalWeight).normalize();
  }

  function updatePathVisualization(pathCells) {
    if (!pathTrail) return;
    const directions = pathCells.map((cell) => getCenteredDirectionForCell(cell.gx, cell.gy));
    pathTrail.setPath(directions);
  }

  const aimRaycaster = new THREE.Raycaster();
  const screenCenter = new THREE.Vector2(0, 0);

  function getBirdseyeAimPoint() {
    if (!earthMesh || !camera) return null;
    aimRaycaster.setFromCamera(screenCenter, camera);
    const hits = aimRaycaster.intersectObject(earthMesh, true);
    return hits.length > 0 ? hits[0].point : null;
  }

  function markPathToAimPoint() {
    if (!birdseyeMode) return;
    if (!navGrid) { console.warn("[nav] navigation grid isn't ready yet."); return; }
    const aimPoint = getBirdseyeAimPoint();
    if (!aimPoint) { console.warn("[nav] crosshair isn't over the maze floor."); return; }

    const rawTargetCell = directionToNavCell(aimPoint.clone().normalize());
    const targetCell = findNearestWalkableCell(rawTargetCell.gx, rawTargetCell.gy);
    const rawStartCell = directionToNavCell(playerState.position.clone().normalize());
    const startCell = findNearestWalkableCell(rawStartCell.gx, rawStartCell.gy);

    const path = findPath(startCell, targetCell);
    if (path.length === 0) console.warn("[nav] no walkable path found between the player and the marked point.");
    else sfx.playPlaceMarker();
    updatePathVisualization(path);
  }

  /* ---------------- hider ---------------- */
  const catchRadius = 0.7;
  // How far (in degrees) the hider's spawn angle is allowed to wander from
  // the exact antipode of the player's spawn, so the two spots are roughly
  // opposite each other on the sphere without being a mirror-perfect pair
  // every round.
  const hiderOppositeJitterDeg = 30;

  let hiderModel = null, hiderMixer = null, hiderIdleAction = null, hiderWalkAction = null, hiderFoundAction = null, hiderCurrentAction = null;
  let hiderModelLoaded = false;
  let hiderPlaced = false;

  const hiderState = { position: new THREE.Vector3(), forward: new THREE.Vector3() };
  let hiderPathPositions = [];
  let hiderPathIndex = 0;

  let elapsedTime = 0;
  let timeRemaining = gameParams.durationSeconds;
  let hasFound = false;
  let hasTimedOut = false;
  let foundTime = null;
  let roundEndScheduled = false;

  function playHiderAnimation(action) {
    if (!action || action === hiderCurrentAction) return;
    if (hiderCurrentAction) hiderCurrentAction.fadeOut(animationFadeDuration);
    action.reset().fadeIn(animationFadeDuration).play();
    hiderCurrentAction = action;
  }

  function spawnHider(gltf) {
    hiderModel = gltf.scene;
    hiderModel.scale.setScalar(params.hiderModelScale);
    hiderModel.traverse((object) => { if (object.isMesh) object.castShadow = true; });
    scene.add(hiderModel);

    hiderMixer = new THREE.AnimationMixer(hiderModel);
    hiderIdleAction = hiderMixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Idle"));
    hiderWalkAction = hiderMixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Walk"));
    hiderFoundAction = hiderMixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Found"));
    hiderFoundAction.setLoop(THREE.LoopOnce);
    hiderFoundAction.clampWhenFinished = true; // holds the final pose - the round ends shortly after anyway

    logMaterialInfo(hiderModel, "hider");
    hiderModelLoaded = true;
    tryInitHider();
  }

  const URGENT_TIME_THRESHOLD = 10;
  const COUNTDOWN_BONUS_FLASH_MS = 650; // matches the .countdown-badge.bonus-flash animation duration in style.css
  const COUNTDOWN_BONUS_POP_MS = 1100; // matches the .countdown-bonus.show animation duration in style.css

  /** Bare, pulsing "time left" number - see .countdown-badge in style.css. */
  function updateCountdownDisplay() {
    countdownElement.style.display = "block";
    countdownElement.textContent = String(Math.max(0, Math.ceil(timeRemaining)));
    countdownElement.classList.toggle("urgent", timeRemaining <= URGENT_TIME_THRESHOLD);
  }
  function hideCountdown() {
    if (countdownElement) countdownElement.style.display = "none";
  }

  /** Trivia time bonus feedback: a "+N" popup next to the countdown and a
   * brief green flash on the number itself, instead of any separate text.
   * classList is reset + reflowed before re-adding "show"/"bonus-flash" so
   * back-to-back gate bonuses each restart their animation from scratch. */
  function flashCountdownBonus(amount) {
    countdownBonusElement.textContent = `+${amount}`;
    countdownBonusElement.classList.remove("show");
    void countdownBonusElement.offsetWidth;
    countdownBonusElement.classList.add("show");
    setTimeout(() => countdownBonusElement.classList.remove("show"), COUNTDOWN_BONUS_POP_MS);

    countdownElement.classList.remove("bonus-flash");
    void countdownElement.offsetWidth;
    countdownElement.classList.add("bonus-flash");
    setTimeout(() => countdownElement.classList.remove("bonus-flash"), COUNTDOWN_BONUS_FLASH_MS);
  }

  function tryInitHider() {
    if (!hiderModelLoaded || !navGrid || hiderPlaced) return;
    placeHiderRandomly();
    hiderPlaced = true;
    elapsedTime = 0;
    timeRemaining = gameParams.durationSeconds;
    updateCountdownDisplay();
    console.log("Where's Bliss? Find him in the maze - watch out for trivia gates!");
  }

  function pickRandomOpenCell() {
    const pool = navGrid.mazeOpenCellKeys?.length > 0 ? navGrid.mazeOpenCellKeys : navGrid.openCellKeys;
    const key = pool[Math.floor(Math.random() * pool.length)];
    return { gx: key % navGridWidth, gy: Math.floor(key / navGridWidth) };
  }

  // The angular jitter alone can still land close to the player after
  // findNearestWalkableCell() snaps it to the nearest open cell - if the
  // jittered antipode happens to fall in an unwalkable stretch of maze
  // whose nearest open cell loops back around toward the player's own
  // corridor, "roughly opposite" quietly stops being true. Rather than
  // trust the angle math alone, each candidate's actual angular separation
  // from the player is checked below and re-rolled if it's too close.
  // 140 (not just "different hemisphere" at 90) keeps this close to a true
  // antipode; more attempts than before since a stricter floor means fewer
  // candidate cells clear it on a sparser maze.
  const hiderMinSeparationRadians = THREE.MathUtils.degToRad(140);
  const hiderPlacementAttempts = 12;

  /** Targets a walkable cell near the antipode of the player's spawn (with
   * a little random jitter so it isn't a mirror-perfect opposite every
   * round), then snaps to the nearest actually-walkable, well-centered
   * cell - see findNearestWalkableCell(). Retries a few times if that
   * snap ends up too close to the player, keeping the best candidate
   * found if none clear the minimum separation. */
  function placeHiderRandomly() {
    const playerDirection = playerState.position.clone().normalize();
    const playerTheta = (Math.atan2(playerDirection.z, playerDirection.x) + Math.PI * 2) % (Math.PI * 2);
    const playerPhi = Math.acos(THREE.MathUtils.clamp(playerDirection.y, -1, 1));

    let bestDirection = null, bestSeparation = -1;
    for (let attempt = 0; attempt < hiderPlacementAttempts; attempt++) {
      const oppositeTheta = playerTheta + Math.PI + THREE.MathUtils.degToRad(THREE.MathUtils.randFloatSpread(hiderOppositeJitterDeg * 2));
      const oppositePhi = THREE.MathUtils.clamp(
        Math.PI - playerPhi + THREE.MathUtils.degToRad(THREE.MathUtils.randFloatSpread(hiderOppositeJitterDeg * 2)),
        mazePolarClearanceRadians,
        Math.PI - mazePolarClearanceRadians
      );
      const oppositeDirection = new THREE.Vector3(
        Math.sin(oppositePhi) * Math.cos(oppositeTheta),
        Math.cos(oppositePhi),
        Math.sin(oppositePhi) * Math.sin(oppositeTheta)
      );

      const rawCell = directionToNavCell(oppositeDirection);
      const candidateCell = findNearestWalkableCell(rawCell.gx, rawCell.gy);
      const candidateDirection = getCenteredDirectionForCell(candidateCell.gx, candidateCell.gy);
      const separation = playerDirection.angleTo(candidateDirection);

      if (separation > bestSeparation) { bestSeparation = separation; bestDirection = candidateDirection; }
      if (separation >= hiderMinSeparationRadians) break;
    }

    const direction = bestDirection;
    hiderState.position.copy(direction).multiplyScalar(getSurfaceRadius(direction));
    hiderState.forward.copy(computeInitialForward(hiderState.position));
    hiderPathPositions = [];
    hiderPathIndex = 0;

    hiderModel.position.copy(hiderState.position);
    hiderModel.up.copy(getUp(hiderState.position));
    hiderModel.lookAt(hiderState.position.clone().add(hiderState.forward));
    playHiderAnimation(hiderIdleAction);
  }

  function startNewHiderWander() {
    const rawCell = directionToNavCell(hiderState.position.clone().normalize());
    const currentCell = findNearestWalkableCell(rawCell.gx, rawCell.gy);

    for (let attempt = 0; attempt < 6; attempt++) {
      const targetCell = pickRandomOpenCell();
      if (targetCell.gx === currentCell.gx && targetCell.gy === currentCell.gy) continue;
      const path = findPath(currentCell, targetCell);
      if (path.length > 1) {
        hiderPathPositions = path.map((cell) => {
          const direction = getCenteredDirectionForCell(cell.gx, cell.gy);
          return direction.multiplyScalar(getSurfaceRadius(direction));
        });
        hiderPathIndex = 0;
        return;
      }
    }
    hiderPathPositions = [];
  }

  function updateHider(deltaTime) {
    if (!hiderModel || !hiderPlaced) return;
    if (hasFound) { playHiderAnimation(hiderFoundAction); return; }
    if (hasTimedOut) { playHiderAnimation(hiderIdleAction); return; }

    if (hiderPathIndex >= hiderPathPositions.length) {
      startNewHiderWander();
      if (hiderPathPositions.length === 0) { playHiderAnimation(hiderIdleAction); return; }
    }

    const target = hiderPathPositions[hiderPathIndex];
    const toTarget = target.clone().sub(hiderState.position);
    const distance = toTarget.length();
    const step = params.hiderMoveSpeed * deltaTime;

    if (distance <= step) {
      hiderState.position.copy(target);
      hiderPathIndex++;
    } else {
      const up = getUp(hiderState.position);
      const direction = toTarget.normalize();
      direction.sub(up.clone().multiplyScalar(direction.dot(up))).normalize();
      hiderState.forward.copy(direction);
      hiderState.position.addScaledVector(direction, step);
      hiderState.position.copy(projectToSurface(hiderState.position));
    }

    playHiderAnimation(hiderPathIndex < hiderPathPositions.length ? hiderWalkAction : hiderIdleAction);

    const up = getUp(hiderState.position);
    hiderModel.position.copy(hiderState.position);
    hiderModel.up.copy(up);
    hiderModel.lookAt(hiderState.position.clone().add(hiderState.forward));
  }

  function scheduleRoundEnd() {
    if (roundEndScheduled) return;
    roundEndScheduled = true;
    setTimeout(() => {
      beginTransitionToEnd();
      onRoundEnd?.({ elapsedSeconds: hasFound ? foundTime : gameParams.durationSeconds, found: hasFound });
    }, ROUND_END_DELAY_MS);
  }

  function tryCatchHider() {
    if (!hiderPlaced || hasFound || hasTimedOut) return;
    const distance = playerState.position.distanceTo(hiderState.position);
    if (distance > catchRadius) {
      console.log(`[hide-and-seek] not close enough yet (${distance.toFixed(2)}m away, need under ${catchRadius}m).`);
      return;
    }
    hasFound = true;
    foundTime = elapsedTime;
    onRoundFinish?.();
    hideCountdown();
    sfx.playFound();
    console.log(`[hide-and-seek] found in ${foundTime.toFixed(2)}s!`);
    scheduleRoundEnd();
  }

  /* ---------------- camera ---------------- */
  const cameraOffset = { distance: 0.55, height: 1.7 };
  const chaseSmoothing = 6;

  const _slerpRelative = new THREE.Vector3();
  const _slerpAxis = new THREE.Vector3();

  /** Spherical-linear-interpolates two unit vectors by fraction t. Used
   * (via flyOverCameraPosition below) so camera flights swing around the
   * globe's curvature instead of cutting a straight line through its
   * interior - a straight lerp between two widely-separated points on/near
   * the sphere's surface passes closer to the centre than either endpoint,
   * which (now that spawn points are random) regularly clipped through the
   * maze. */
  function slerpDirection(a, b, t, target) {
    const dot = THREE.MathUtils.clamp(a.dot(b), -1, 1);
    if (dot > 0.9995) return target.copy(a).lerp(b, t).normalize();
    if (dot < -0.9995) {
      _slerpAxis.crossVectors(new THREE.Vector3(1, 0, 0), a);
      if (_slerpAxis.lengthSq() < 1e-6) _slerpAxis.crossVectors(new THREE.Vector3(0, 1, 0), a);
      _slerpAxis.normalize();
      return target.copy(a).applyAxisAngle(_slerpAxis, Math.PI * t);
    }
    const theta = Math.acos(dot) * t;
    _slerpRelative.copy(b).addScaledVector(a, -dot).normalize();
    return target.copy(a).multiplyScalar(Math.cos(theta)).addScaledVector(_slerpRelative, Math.sin(theta));
  }

  const _flyFromDir = new THREE.Vector3();
  const _flyToDir = new THREE.Vector3();

  /** One interpolation step (t in [0,1], or a per-frame smoothing fraction)
   * from `from` towards `to`, keeping the camera's distance from the
   * globe's centre a plain lerp of the two endpoints' distances while its
   * direction swings around via slerpDirection - so it flies over the
   * globe's curvature rather than through it. */
  function flyOverCameraPosition(from, to, t, target) {
    const fromRadius = from.length();
    const toRadius = to.length();
    _flyFromDir.copy(from).normalize();
    _flyToDir.copy(to).normalize();
    slerpDirection(_flyFromDir, _flyToDir, t, target);
    return target.multiplyScalar(THREE.MathUtils.lerp(fromRadius, toRadius, t));
  }
  const transitionChaseSmoothing = 2; // gentler, more cinematic easing while flying in from the menu
  let activeChaseSmoothing = chaseSmoothing;
  const lookAheadDistance = 0.3;
  const lookAheadHeight = 0.15;
  const birdseyeCameraOffset = { distance: 1.5 };

  function getMenuCameraPosition() {
    return new THREE.Vector3(
      Math.sin(menuOrbitAngle) * menuOrbitRadius,
      menuOrbitHeight,
      Math.cos(menuOrbitAngle) * menuOrbitRadius
    );
  }
  function updateMenuCamera(deltaTime) {
    if (!camera) return;
    menuOrbitAngle += menuOrbitSpeed * deltaTime;
    camera.position.copy(getMenuCameraPosition());
    camera.up.set(0, 1, 0);
    camera.lookAt(0, menuLookAtHeight, 0);
  }

  function getLookAtTarget() {
    const up = getUp(playerState.position);
    const renderPosition = getRenderPosition();
    return renderPosition.clone().addScaledVector(up, lookAheadHeight).addScaledVector(playerState.forward, lookAheadDistance);
  }
  function getChaseCameraTarget() {
    const up = getUp(playerState.position);
    const renderPosition = getRenderPosition();
    return renderPosition.clone().addScaledVector(up, cameraOffset.height).addScaledVector(playerState.forward, -cameraOffset.distance);
  }
  function getBirdseyeLookAtTarget() { return projectToSurface(birdseyeState.position); }
  function getBirdseyeCameraTarget() {
    const up = getUp(birdseyeState.position);
    const surfacePoint = projectToSurface(birdseyeState.position);
    return surfacePoint.clone().addScaledVector(up, birdseyeHeight).addScaledVector(birdseyeState.forward, -birdseyeCameraOffset.distance);
  }
  function updateBirdseyeCamera(deltaTime) {
    let up = getUp(birdseyeState.position);
    const { turn, move } = getMoveInput();
    if (turn !== 0) birdseyeState.forward.applyAxisAngle(up, turn * birdseyeTurnSpeed * deltaTime);
    birdseyeState.forward.sub(up.clone().multiplyScalar(birdseyeState.forward.dot(up))).normalize();

    if (move !== 0) {
      birdseyeState.position.copy(projectToSurface(birdseyeState.position.clone().addScaledVector(birdseyeState.forward, move * birdseyeMoveSpeed * deltaTime)));
    }
    up = getUp(birdseyeState.position);
    birdseyeState.forward.sub(up.clone().multiplyScalar(birdseyeState.forward.dot(up))).normalize();
  }
  function updateCamera(deltaTime) {
    if (!model || !camera) return;
    const t = 1 - Math.exp(-activeChaseSmoothing * deltaTime);
    if (birdseyeMode) {
      flyOverCameraPosition(camera.position, getBirdseyeCameraTarget(), t, camera.position);
      camera.up.copy(getUp(birdseyeState.position));
      camera.lookAt(getBirdseyeLookAtTarget());
    } else {
      flyOverCameraPosition(camera.position, getChaseCameraTarget(), t, camera.position);
      camera.up.copy(getUp(playerState.position));
      camera.lookAt(getLookAtTarget());
    }
  }

  /** Camera flight for the "toEnd" mode (round finishing -> leaderboard) -
   * mirrors updateCamera()'s chase-cam step, just flying towards the menu
   * orbit pose instead of the player. */
  function updateEndTransitionCamera(deltaTime) {
    if (!camera) return;
    const t = 1 - Math.exp(-activeChaseSmoothing * deltaTime);
    flyOverCameraPosition(camera.position, getMenuCameraPosition(), t, camera.position);
    camera.up.set(0, 1, 0);
    camera.lookAt(0, menuLookAtHeight, 0);
  }

  /* ---------------- menu <-> gameplay flow ---------------- */
  /** Menu/leaderboard should read as "just the earth and maze" - not the
   * previous round's leftovers - so both hide the player/hider models, any
   * marked path trail, and reset every gate closed. Actual gameplay state
   * (hiderPlaced etc.) still gets freshly rebuilt by resetRound() when the
   * next round actually starts; this just clears what's on screen. */
  function hideGameplayActors() {
    if (model) model.visible = false;
    if (hiderModel) hiderModel.visible = false;
    if (pathTrail) pathTrail.clear();
    if (crosshairElement) hideBirdseyeHud();
    if (gatePromptElement) gatePromptElement.style.display = "none";
    if (catchPromptElement) catchPromptElement.style.display = "none";
    activeGate = null;
    isNearHider = false;
    birdseyeMode = false;
    birdseyeEntryPending = false;
    isThrowing = false;
    isCatching = false;
    isDrinking = false;
    if (hatArmature) hatArmature.visible = true;
    if (coconutProp) coconutProp.visible = false;
    sfx.setFootstepsPlaying(false);
    hidePickups();
    for (const gate of gates) {
      gate.opened = false;
      gate.doorOpenProgress = 0;
      if (gate.doorVisual && gate.doorClosedQuat) gate.doorVisual.quaternion.copy(gate.doorClosedQuat);
    }
  }

  function enterMenuMode() {
    mode = "menu";
    transitionElapsed = 0;
    activeChaseSmoothing = chaseSmoothing;
    menuOrbitAngle = 0;
    menuEl.classList.remove("hidden");
    hideCountdown();
    hideMobileControls();
    hideGameplayActors();
    if (camera) {
      camera.position.copy(getMenuCameraPosition());
      camera.up.set(0, 1, 0);
      camera.lookAt(0, menuLookAtHeight, 0);
    }
  }

  // Round-end screen (leaderboard) reuses this same orbiting showcase pose
  // instead of a separate scene, so the earth stays visible behind the
  // leaderboard card exactly like it does behind the menu. Getting there is
  // an animated flight (see the "toEnd" mode below), same as menu -> play,
  // so the camera swings around the globe's curvature instead of cutting
  // straight from wherever the chase cam was (which, with random spawns,
  // can be almost anywhere) to the orbit pose.
  function beginTransitionToEnd() {
    mode = "toEnd";
    transitionElapsed = 0;
    activeChaseSmoothing = transitionChaseSmoothing;
    hideCountdown();
    hideMobileControls();
    hideGameplayActors();
  }

  function finishTransitionToEnd() {
    mode = "end";
    activeChaseSmoothing = chaseSmoothing;
    if (camera) {
      camera.position.copy(getMenuCameraPosition());
      camera.up.set(0, 1, 0);
      camera.lookAt(0, menuLookAtHeight, 0);
    }
  }

  function beginTransitionToPlay() {
    // menuEl is already hidden by beginRoundStart() - kept hidden through
    // the asset-load wait so the title/PLAY button aren't still sitting
    // there when the transition camera kicks in.
    placePlayerAtMazeSpawn();
    mode = "transition";
    transitionElapsed = 0;
    activeChaseSmoothing = transitionChaseSmoothing;
  }

  function finishTransitionToPlay() {
    activeChaseSmoothing = chaseSmoothing;
    mode = "play";
    showMobileControls();
    resetRound();
  }

  /** Waits out any background asset loading still in flight (see
   * loadAssets()'s critical/deferred split) before starting the transition
   * - PLAY stays disabled for that brief window rather than the round
   * silently starting before its assets are ready. */
  function beginRoundStart() {
    if (isStartingRound) return;
    isStartingRound = true;
    menuEl.classList.add("hidden");
    playBtn.disabled = true;
    // .catch() so a background load failure (player/hider/pickup/trivia)
    // still proceeds into the round best-effort instead of leaving PLAY
    // stuck disabled forever with no feedback - errors are already logged
    // where each of those loads happens.
    Promise.resolve(assetsLoadingPromise).catch(() => {}).then(() => {
      isStartingRound = false;
      playBtn.disabled = false;
      if (mode === "menu") beginTransitionToPlay();
    });
  }

  function requestStartGame() {
    if (mode !== "menu") return;
    loadAssets(); // no-op if already loading/loaded - just makes sure it's kicked off
    beginRoundStart();
  }

  playBtn.addEventListener("click", () => {
    sfx.retryWindAutoplay();
    musicController.retryAutoplay();
    requestStartGame();
  });

  /* ---------------- movement ---------------- */
  function updatePlayerMovement(deltaTime) {
    if (!model) return;
    let up = getUp(playerState.position);
    const { turn, move } = getMoveInput();
    if (turn !== 0) playerState.forward.applyAxisAngle(up, turn * turnSpeed * deltaTime);
    playerState.forward.sub(up.clone().multiplyScalar(playerState.forward.dot(up))).normalize();

    // Resolved every frame, not just while a movement key is held - a
    // stationary player can otherwise sit embedded in a wall (e.g. left
    // slightly inside a bush by a dash) with nothing ever pushing them back
    // out, since collision was previously only re-checked on the frames
    // that actually proposed a new position.
    const proposedSurface = move !== 0
      ? projectToSurface(playerState.position.clone().addScaledVector(playerState.forward, move * params.playerMoveSpeed * deltaTime))
      : projectToSurface(playerState.position);
    playerState.position.copy(resolveWallCollisions(proposedSurface));

    up = getUp(playerState.position);
    playerState.forward.sub(up.clone().multiplyScalar(playerState.forward.dot(up))).normalize();

    playerState.verticalVelocity -= gravity * deltaTime;
    playerState.height += playerState.verticalVelocity * deltaTime;
    if (playerState.height <= 0) { playerState.height = 0; playerState.verticalVelocity = 0; playerState.isGrounded = true; playerState.jumpsUsed = 0; }

    syncPlayerModelTransform();
  }

  /** Drops the player at a random walkable cell within the maze band
   * (never the open polar caps) - called right before the camera flies
   * into gameplay, so the player never starts out "on the earth" rather
   * than in the maze, and lands somewhere new every round. The hider is
   * then placed near the antipode of wherever this lands - see
   * placeHiderRandomly(). */
  function placePlayerAtMazeSpawn() {
    if (navGrid) {
      const cell = pickRandomOpenCell();
      const direction = getCenteredDirectionForCell(cell.gx, cell.gy);
      playerState.position.copy(direction).multiplyScalar(getSurfaceRadius(direction));
    }
    playerState.forward.copy(computeInitialForward(playerState.position));
    playerState.height = 0;
    playerState.verticalVelocity = 0;
    playerState.isGrounded = true;
    playerState.jumpsUsed = 0;
    if (model) model.visible = true;
    syncPlayerModelTransform();
  }

  /* ---------------- asset loading (runs once, and is properly awaited) ----------------
   * Split into "critical" (sky + maze - the menu's visible orbiting backdrop) and the
   * rest (player/hider/pickup models, trivia questions - invisible until a round
   * actually starts). enter() only blocks the loading overlay on the critical half, so
   * the menu appears as soon as the world is visible instead of waiting on models that
   * aren't even shown until PLAY is pressed; those keep loading in the background and
   * are picked up by beginRoundStart() before a round actually begins. */
  async function loadAssets() {
    if (assetsLoaded) return;
    if (assetsLoadingPromise) return assetsLoadingPromise;

    let resolveCriticalAssets;
    criticalAssetsPromise = new Promise((resolve) => { resolveCriticalAssets = resolve; });

    assetsLoadingPromise = (async () => {
      scene = new THREE.Scene();
      pathTrail = createPathTrail({ getSurfaceRadius });
      scene.add(pathTrail.group);

      bushMaterial = createGroundToonMaterial();
      gateMaterial = createGateToonMaterial();

      // The visible backdrop stays a flat sky blue (cheapest possible - one
      // clear colour, no shader/texture at all). three.js's built-in Sky is
      // still used, but only offscreen: it's baked once into a small PMREM
      // environment map for the player/hider models' lighting/reflections,
      // replacing the old always-resident EXR HDRI. Never added to the
      // scene, so it costs nothing per frame.
      scene.background = new THREE.Color(0x8fc7f0);

      const sky = new Sky();
      sky.scale.setScalar(90);
      const skyUniforms = sky.material.uniforms;
      skyUniforms.turbidity.value = 4;
      skyUniforms.rayleigh.value = 2;
      skyUniforms.mieCoefficient.value = 0.003;
      skyUniforms.mieDirectionalG.value = 0.8;

      const sunElevationDeg = 45;
      const sunAzimuthDeg = 140;
      const sunPhi = THREE.MathUtils.degToRad(90 - sunElevationDeg);
      const sunTheta = THREE.MathUtils.degToRad(sunAzimuthDeg);
      const sunDirection = new THREE.Vector3().setFromSphericalCoords(1, sunPhi, sunTheta);
      skyUniforms.sunPosition.value.copy(sunDirection);

      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      scene.environment = pmremGenerator.fromScene(sky).texture;
      pmremGenerator.dispose();

      // The ground/gate toon shaders bake their own fixed lighting
      // (see materials.js) and ignore scene lights entirely - these two
      // lights only shape the player/hider models. Since the player walks
      // all the way around a sphere, a strong single-direction sun would
      // leave the far side dark; a hemisphere light (which reacts to each
      // surface's own normal, not view direction) reads as even lighting
      // from any point on the globe, with the directional sun kept soft
      // and only used for a bit of shape/highlight on top.
      scene.add(new THREE.HemisphereLight(0x9fd3f5, 0x6b8f52, 1.1));
      const sunLight = new THREE.DirectionalLight(0xfff3da, 0.45);
      sunLight.position.copy(sunDirection).multiplyScalar(20);
      scene.add(sunLight);

      buildOverlays();
      const triviaPromise = loadTriviaQuestions();
      playerState.forward.copy(computeInitialForward(playerState.position));

      if (DEBUG_GUI) {
        window.__debugPlayerState = playerState;
        window.__debugGates = gates;
        window.__debugPickups = pickups;
        window.__debugPickupParams = pickupParams;
        window.__debugScene = scene;
        window.__debugCamera = camera;
        window.__debugTHREE = THREE;
        window.__debugSfx = sfx;
        window.__debugHiderState = hiderState;
        window.__debugBirdseyeState = birdseyeState;
        window.__debugRoundState = {
          get hasFound() { return hasFound; },
          get hasTimedOut() { return hasTimedOut; },
          get isNearHider() { return isNearHider; },
          get hiderActionName() { return hiderCurrentAction ? hiderCurrentAction.getClip().name : null; },
        };
        window.__debugPlayerAnim = {
          get isThrowing() { return isThrowing; },
          get currentActionName() { return currentAction ? currentAction.getClip().name : null; },
          get birdseyeMode() { return birdseyeMode; },
          get birdseyeEntryPending() { return birdseyeEntryPending; },
          get isCatching() { return isCatching; },
          get isDrinking() { return isDrinking; },
          get hatVisible() { return hatArmature ? hatArmature.visible : null; },
          get coconutPropVisible() { return coconutProp ? coconutProp.visible : null; },
        };
        gui = new GUI();
        const playerGuiFolder = gui.addFolder("Player");
        playerGuiFolder.add(params, "playerMoveSpeed", 0.1, 5, 0.05).name("Move Speed");
        playerGuiFolder.add(params, "playerModelScale", 0.05, 1, 0.01).name("Model Scale").onChange((value) => { if (model) model.scale.setScalar(value); });
        const hiderGuiFolder = gui.addFolder("Hider (Bliss)");
        hiderGuiFolder.add(params, "hiderMoveSpeed", 0.1, 5, 0.05).name("Move Speed");
        hiderGuiFolder.add(params, "hiderModelScale", 0.05, 1, 0.01).name("Model Scale").onChange((value) => { if (hiderModel) hiderModel.scale.setScalar(value); });
        const gameGuiFolder = gui.addFolder("Game");
        gameGuiFolder.add(gameParams, "durationSeconds", 15, 180, 5).name("Time Limit (s)");
        const gateGuiFolder = gui.addFolder("Trivia Gates");
        gateGuiFolder.add(gateParams, "interactRadius", 0.3, 2, 0.05).name("Interact Radius");
        gateGuiFolder.add(gateParams, "timeBonusSeconds", 0, 30, 1).name("Correct Bonus (s)");
        const pickupGuiFolder = gui.addFolder("Coconut Water");
        pickupGuiFolder.add(pickupParams, "interactRadius", 0.3, 2, 0.05).name("Interact Radius");
        pickupGuiFolder.add(pickupParams, "timeBonusSeconds", 0, 30, 1).name("Time Bonus (s)");
        pickupGuiFolder.add(pickupParams, "modelScale", 0.05, 1, 0.01).name("Model Scale").onChange((value) => {
          for (const pickup of pickups) pickup.mesh.scale.setScalar(value);
        });
      }

      const playerLoadPromise = gltfLoader.loadAsync(playerModelPath).then((gltf) => {
        model = gltf.scene;
        model.scale.setScalar(params.playerModelScale);
        gltf.scene.traverse((object) => { if (object.isMesh) object.castShadow = true; });
        // Hidden until placePlayerAtMazeSpawn() gives it a real spot in the
        // maze band - otherwise it'd flash into view at the stale default
        // position while the menu's showcase camera is still orbiting.
        model.visible = false;
        scene.add(gltf.scene);
        hatArmature = model.getObjectByName("HatArmature");
        coconutProp = model.getObjectByName("Coco_Water");
        if (coconutProp) coconutProp.visible = false; // only shown during the Drink clip - see playDrinkAnimation()

        mixer = new THREE.AnimationMixer(gltf.scene);
        idleAction = mixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Idle"));
        walkAction = mixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Walk"));
        jumpAction = mixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Jump"));
        jumpAction.setLoop(THREE.LoopOnce);
        jumpAction.clampWhenFinished = true;
        throwAction = mixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Throw"));
        throwAction.setLoop(THREE.LoopOnce);
        throwAction.clampWhenFinished = true;
        catchAction = mixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Catch"));
        catchAction.setLoop(THREE.LoopOnce);
        catchAction.clampWhenFinished = true;
        drinkAction = mixer.clipAction(THREE.AnimationClip.findByName(gltf.animations, "Drink"));
        drinkAction.setLoop(THREE.LoopOnce);
        drinkAction.clampWhenFinished = true;
        mixer.addEventListener("finished", (event) => {
          if (event.action === throwAction) {
            isThrowing = false;
            if (birdseyeEntryPending) enterBirdseyeAfterThrow();
          } else if (event.action === catchAction) {
            isCatching = false; // hat is already visible - see toggleBirdseye()
          } else if (event.action === drinkAction) {
            isDrinking = false;
            if (coconutProp) coconutProp.visible = false;
            sfx.playDrink(); // plays as the swig finishes, not at pickup - see playDrinkAnimation()
          }
        });

        playAnimation(idleAction);
        logMaterialInfo(gltf.scene, "player");
      });

      const hiderLoadPromise = gltfLoader.loadAsync(hiderModelPath).then((gltf) => spawnHider(gltf));
      const pickupLoadPromise = gltfLoader.loadAsync(pickupModelPath).then((gltf) => spawnPickupModels(gltf));

      const mazeLoadPromise = gltfLoader.loadAsync("/models/Earth/Maze.glb").then((gltf) => {
        const root = gltf.scene;
        const earth = root.getObjectByName("Earth");
        const mazeVisual = root.getObjectByName("MazeVisual");
        const mazeCollision = root.getObjectByName("MazeCollision");
        const spawnPlat = root.getObjectByName("SpawnPlat");
        const grass = root.getObjectByName("Grass_PLANE");
        // GLTFLoader strips characters like "." from node names (they're
        // reserved for animation-binding paths), so the second grass patch
        // - authored in Blender as "Grass_PLANE.001" - loads in as this.
        const grassAlt = root.getObjectByName("Grass_PLANE001");
        const rocks = root.getObjectByName("Rocks_PLANE");
        const rocksAlt = root.getObjectByName("Rocks_PLANE001"); // "Rocks_PLANE.001", same "." stripping as above
        const cloudsMesh = root.getObjectByName("Clouds");

        root.traverse((object) => { if (object.isMesh) { object.castShadow = true; object.receiveShadow = true; } });

        root.updateWorldMatrix(true, true);
        let totalDistance = 0, sampleCount = 0;
        const sampleStride = 25, worldPos = new THREE.Vector3();
        (earth || root).traverse((object) => {
          if (!object.isMesh) return;
          const positions = object.geometry.attributes.position;
          for (let i = 0; i < positions.count; i += sampleStride) {
            worldPos.fromBufferAttribute(positions, i);
            object.localToWorld(worldPos);
            totalDistance += worldPos.length();
            sampleCount++;
          }
        });
        const averageRadius = totalDistance / sampleCount;
        root.scale.multiplyScalar(sphereRadius / averageRadius);
        root.position.set(0, 0, 0);
        root.updateWorldMatrix(true, true);

        scene.add(root);

        if (mazeCollision) {
          mazeCollision.traverse((object) => { if (object.isMesh) object.visible = false; });
          collisionMeshData = buildCollisionMeshData(mazeCollision);
          const triCount = collisionMeshData.reduce((sum, c) => sum + c.triangles.length, 0);
          console.log(`[maze] built ${collisionMeshData.length} collider(s), ${triCount} triangles total from "MazeCollision"`);
        } else {
          console.warn('[maze] no object named "MazeCollision" was found - wall collision will not work.');
        }

        if (earth) earth.traverse((object) => { if (object.isMesh) object.material = earthMaterial; });
        if (mazeVisual) mazeVisual.traverse((object) => { if (object.isMesh) object.material = bushMaterial; });
        if (spawnPlat) spawnPlat.traverse((object) => { if (object.isMesh) object.material = platformMaterial; });
        if (grass) grass.traverse((object) => { if (object.isMesh) object.material = bushMaterial; });
        if (grassAlt) grassAlt.traverse((object) => { if (object.isMesh) object.material = bushMaterial; });
        if (rocks) rocks.traverse((object) => { if (object.isMesh) object.material = gateMaterial; });
        if (rocksAlt) rocksAlt.traverse((object) => { if (object.isMesh) object.material = gateMaterial; });

        if (cloudsMesh) clouds = createClouds({ cloudsMesh });
        else console.warn('[maze] no object named "Clouds" was found - no cloud shell will be present.');

        earthMesh = earth || root;

        const groundObjects = findGroundObjects(root, earth, spawnPlat);
        buildTerrainHeightMap(groundObjects);
        conformSphereWorldToTerrain();

        loadGatesFromMazeRoot(root);
        buildNavigationGrid();

        logMaterialInfo(root, "earth");
      });

      // Sky + maze is everything the menu's orbiting backdrop needs to look
      // right - resolving this separately (rather than waiting on the
      // player/hider/pickup models and trivia fetch below, none of which
      // are visible until a round actually starts) is what lets enter()
      // show the menu without waiting on those too. .finally(), not
      // .then(): a maze load failure still needs to unblock enter()'s
      // await (it's already logged via gltfLoader.manager.onError above),
      // otherwise the loading overlay would spin forever instead of at
      // least reaching a broken-but-visible menu.
      mazeLoadPromise.finally(() => {
        criticalAssetsLoaded = true;
        resolveCriticalAssets();
      });

      await Promise.all([triviaPromise, playerLoadPromise, hiderLoadPromise, pickupLoadPromise, mazeLoadPromise]);
      assetsLoaded = true;
    })();

    return assetsLoadingPromise;
  }

  /* ---------------- round lifecycle ---------------- */
  function resetRound() {
    onRoundStart?.();
    hasFound = false;
    hasTimedOut = false;
    foundTime = null;
    roundEndScheduled = false;
    isTriviaModalOpen = false;
    birdseyeMode = false;
    birdseyeEntryPending = false;
    isThrowing = false;
    isCatching = false;
    isDrinking = false;
    if (hatArmature) hatArmature.visible = true;
    if (coconutProp) coconutProp.visible = false;
    if (model) model.visible = true;
    if (hiderModel) hiderModel.visible = true;
    if (pathTrail) pathTrail.clear();
    if (crosshairElement) hideBirdseyeHud();
    if (triviaOverlay) triviaOverlay.style.display = "none";

    playerState.height = 0;
    playerState.verticalVelocity = 0;
    playerState.isGrounded = true;
    playerState.jumpsUsed = 0;
    playerState.position.copy(projectToSurface(playerState.position.length() > 0 ? playerState.position : new THREE.Vector3(0, sphereRadius, 0)));
    playerState.forward.copy(computeInitialForward(playerState.position));

    for (const gate of gates) {
      gate.opened = false;
      gate.doorOpenProgress = 0;
      gate.doorVisual.quaternion.copy(gate.doorClosedQuat);
    }
    assignRandomQuestionsToGates();

    if (navGrid) {
      hiderPlaced = false;
      tryInitHider();
      spawnPickups();
    }
  }

  /* ---------------- public interface ---------------- */
  return {
    get scene() { return scene; },
    get camera() { return camera; },

    async enter(manager) {
      renderer = manager.renderer;
      if (!camera) {
        camera = new THREE.PerspectiveCamera(75, manager.sizes.width / manager.sizes.height, 0.1, 100);
      }

      // Idempotent/memoized - the first call kicks off the real load. Only
      // the critical (sky + maze) half is awaited here, so the loading
      // overlay covers just long enough for the menu's visible backdrop to
      // be ready, not the player/hider/pickup models or trivia questions
      // (invisible until PLAY is actually pressed) - those keep loading in
      // the background and beginRoundStart() waits on them before a
      // round starts. Later calls (e.g. coming back from the leaderboard)
      // resolve immediately since everything's already loaded, so this is
      // a no-op then.
      if (!criticalAssetsLoaded) {
        loadingOverlay.show();
        loadAssets();
        await criticalAssetsPromise;
        loadingOverlay.hide();
      }

      if (!scene.children.includes(camera)) scene.add(camera);

      attachInput();
      attachOverlays();
      enterMenuMode();
    },

    exit() {
      detachInput();
      detachOverlays();
      menuEl.classList.add("hidden");
    },

    // Called by leaderboardScreen's "back to menu" button - the leaderboard
    // is a DOM overlay on top of this same persistent scene, not a separate
    // SceneManager screen, so this is a mode change rather than a switchTo.
    returnToMenu() {
      enterMenuMode();
    },

    onResize(sizes) {
      if (!camera) return;
      camera.aspect = sizes.width / sizes.height;
      camera.updateProjectionMatrix();
    },

    update(deltaTime) {
      if (pathTrail) pathTrail.update(deltaTime);
      if (clouds) clouds.update(deltaTime);

      if (mode === "menu" || mode === "end") {
        updateMenuCamera(deltaTime);
        return;
      }

      if (mode === "toEnd") {
        transitionElapsed += deltaTime;
        updateEndTransitionCamera(deltaTime);
        if (transitionElapsed >= TRANSITION_DURATION) finishTransitionToEnd();
        return;
      }

      if (mode === "transition") {
        transitionElapsed += deltaTime;
        if (mixer) mixer.update(deltaTime);
        if (hiderMixer) hiderMixer.update(deltaTime);
        updateCamera(deltaTime);
        if (transitionElapsed >= TRANSITION_DURATION) finishTransitionToPlay();
        return;
      }

      if (mixer) mixer.update(deltaTime);
      updateAnimationState();
      if (hiderMixer) hiderMixer.update(deltaTime);
      updateHider(deltaTime);
      // Catch proximity first - updateGateProximity() reads isNearHider to
      // suppress the gate when both are in range (see its doc comment).
      updateCatchProximity();
      updateGateProximity();
      if (hiderPlaced && !hasFound && !hasTimedOut && !isTriviaModalOpen) updatePickups(deltaTime);

      for (const gate of gates) {
        if (!gate.opened || gate.doorOpenProgress >= 1) continue;
        gate.doorOpenProgress = Math.min(1, gate.doorOpenProgress + gate.doorOpenSpeed * deltaTime);
        gate.doorVisual.quaternion.copy(gate.doorClosedQuat).slerp(gate.doorOpenQuat, gate.doorOpenProgress);
      }

      // Deliberately not gated on !isTriviaModalOpen - a gate is meant to
      // cost time (that's the whole point of the correct-answer bonus
      // offsetting it), not freeze the clock while it's up.
      if (hiderPlaced && !hasFound && !hasTimedOut) {
        elapsedTime += deltaTime;
        timeRemaining -= deltaTime;
        if (timeRemaining <= 0) {
          timeRemaining = 0;
          hasTimedOut = true;
          // The round is ending out from under the trivia modal - force it
          // closed rather than leaving it stuck on top of the round-end
          // transition/leaderboard screen.
          if (isTriviaModalOpen) closeTriviaModal();
          onRoundFinish?.();
          hideCountdown();
          sfx.playTimeout();
          scheduleRoundEnd();
        }
      }

      if (hiderPlaced && !hasFound && !hasTimedOut) {
        updateCountdownDisplay();
      }

      if (birdseyeMode) updateBirdseyeCamera(deltaTime);
      else if (!isTriviaModalOpen && !birdseyeEntryPending && !isCatching) updatePlayerMovement(deltaTime);
      updateCamera(deltaTime);
    },
  };
}
